import type { CSSProperties, JSX } from 'react';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Button, Menu, MenuItem, Text, Tooltip, View } from '../ui/index';
import type {
  CustomHeaderRendererProps,
  ItemRendererProps,
  ReactCalendarGroupRendererProps,
  TimelineContextValue,
  TimelineGroup,
  TimelineGroupBase,
  TimelineItem,
} from './react-calendar-timeline';
import { CustomHeader, SidebarHeader } from './react-calendar-timeline';
import { fullSize } from './timelineStyles';
import { TimelineStyle } from './timelineStyles';
import { SidebarContainer, UnselectableDiv } from './Common.styled';
import { RegionRow } from './Regions.styled';
import { NotFoundTimelineItems } from './NotFoundTimelineItems';
import {
  getSumOffset,
  getSumScroll,
} from './react-calendar-timeline/lib/utility/dom-helpers';
import useContextMenu from '../hooks/useContextMenu';
import type { ScenarioSchedule } from '@seaber/common/core/scenario';
import { type Order, type SwapDirection } from '../core/Core';
import type { Vessel } from '../core/Vessel';
import { getVesselCapacities } from '../core/Vessel';
import type {
  Suggestion,
  SuggestionsByVessel,
} from '../core/suggestions/getSuggestions';
import type { ScheduleDataContext } from '../core/ScheduleDataContext';
import type { SingleEvent, SingleEventType, TimeData } from '../core/TimeData';
import {
  isOffHireEvent,
  isSingleEvent,
  isSingleEventTimeData,
  isStaticEvent,
} from '../core/TimeData';
import type { OffHireEntry } from '@seaber/common/core/entries';
import { hToMs, msToH } from '../../lib/time';
import { Objective } from '../core/suggestions/VoyageSuggestionParams';
import { scrollToOrders, scrollToToday, useTimelineHandlers } from './handlers';
import { useModal } from '../contexts/ModalContext';
import type {
  OpenPositionsFragment,
  ScenarioTimeline_CoaFragment,
  ScenarioTimeline_OrganizationFragment,
  VesselReservationFragment,
} from '../../graphql';
import { useAllRegionsQuery } from '../../graphql';
import { InfoIcon, NoteIcon } from '../Icons';
import {
  assertDefined,
  assertedDefined,
  countWith,
  filteredMap,
  formatHToDaysHour,
  mapKeyBy,
  NONE,
  sortBy,
  useDeepMemo,
} from '../../lib/util';
import keyBy from 'lodash/keyBy';
import type { EventTimeline, TimelineFocus } from '../core/Timelines';
import uniq from 'lodash/uniq';
import { dateStringToNumOpt } from '../core/util';
import { compact, noop } from 'lodash';
import { useOverridableState } from '../hooks/useOverridableState';
import type { ClientId } from '../core/ClientId';
import { parseClientId } from '../core/ClientId';
import { toast } from 'react-toastify';
import TimeField, { timeFieldFormat } from '../ui/TimeField';
import type { UseDropProps } from '../hooks/useDragDrop';
import {
  getDropData,
  setDragData,
  setDropEffect,
  useDrop,
} from '../hooks/useDragDrop';
import type * as z from 'zod';
import { singleEventTypes } from '../shipment/ShippingPlanTypes';
import type {
  LocationData,
  RegionBalanceFilter,
  RegionHeaderData,
  WithRegionNames,
} from './Regions';
import {
  calculateRegionOpenData,
  RegionHeaderIntervals,
  RegionHeaderToggle,
  RegionsListHeaderSidebar,
} from './Regions';
import type { RegionPolygons } from '../GoogleMaps';
import type { ScenarioTimelineDiff } from '../ScenarioUtil';
import type {
  ResetAssignmentsToMasterOpts,
  ScenarioMenuItemType,
} from '../../pages/planning';
import type { ITimelineView } from './TimelineView';
import TimelineView, { sortTimelineGroups } from './TimelineView';
import TimelineResizeDetector from './TimelineResizeDetector';
import type { CustomObserver } from './IntersectionObserver';
import {
  IntersectionObserverBox,
  prepareIntersectionObserver,
} from './IntersectionObserver';
import {
  isWaitLaycanDefaultAtom,
  useIsVesselCapacityVisibleState,
  useIsVesselRemarksVisibleState,
  useRegionToolIntervalState,
} from '../../state/planning/options';
import type {
  CompletedData,
  ScenarioOrder,
  ScenarioShipment,
} from '../../lib/planningData';
import { handleDeleteOpenPosition } from '../openPosition/cudOpenPositions';
import { handleDeleteVesselReservation } from '../vesselReservation/deleteVesselReservation';
import {
  incomeForLoadedAmountGetter,
  isEuEtsCostEnabledForUser,
} from '../shipment/income/income';
import {
  hasFeature,
  useCurrencyRates,
  useCurrentUser,
  useDefaultCurrency,
  usePlanningMode,
} from '../../state/zustand';
import { useBunkerPrices } from '../hooks/useBunkerPrices';
import useEvent from '../hooks/useEvent';
import { vesselReservationTypes } from '../../constants/vesselReservationTypes';
import { useFocusedOrders } from '../../state/planning/timeline';
import { DeactivateVesselModal } from './vesselPrompt/DeactivateVesselModal';
import { ModifyVesselToFleetModal } from './vesselPrompt/RemoveVesselFromFleetModal';
import { timeToCoordinateRatio } from './react-calendar-timeline/lib/utility/calendar';
import type { PnlData } from '../hooks/usePnlData';
import { shouldWaitForLaycan } from '../PlanningTimelineOptions';
import { useAtomValue } from 'jotai';
import { DefaultMap, mapGroupBy } from '@seaber/common/lib/collections';
import { getFreightsByOrderId } from '../shipment/income/orderFreight';
import {
  SuggestionControls,
  suggestionShadowStyles,
  TimelineSuggestionRenderer,
} from './suggestions/TimelineSuggestion';
import type {
  ScenarioTimelineGroup,
  ScenarioTimelineGroupFields,
  ScenarioTimelineItem,
  ShipmentIntegrationStatus,
  TimelineEvent,
  TimelineOffHireEvent,
  TimelineOpenPosition,
  TimelineTcInEvent,
  TimelineVesselReservation,
} from './types';
import { ItemType } from './types';
import {
  indicatorTypeToColor,
  PortNameIndicator,
  TimelineLineIndicator,
} from './Indicators';
import {
  TimelineCompletedVoyageRenderer,
  TimelineVoyagePlaceholder,
  TimelineVoyageRenderer,
} from './TimelineVoyage';
import type { dropOrdersToTimelineSchema } from './utils/utils';
import { dragDropDataSchema, snapTimeTo1HGrid } from './utils/utils';
import {
  useCompletedItems,
  useFilteredItems,
  useSingleClickHandlers,
  useSuggestionItems,
  useOpenSuggestionEstimator,
} from './utils/hooks';
import { buildAssignedItems } from './utils/buildAssignedItems';
import type { VoyageMenuContextValue } from '../contexts/VoyageMenuContext';
import { VoyageMenuContext } from '../contexts/VoyageMenuContext';
import { useContextStore } from '../contexts/ZustandContext';
import { useCurrentScenarioPnl } from '../pnl/ui/context';

export type VoyagePlacement = 'exact' | 'laycan' | 'asap';

export interface ScenarioTimelineProps {
  onCreateAssignment: (
    assignments: Array<{
      orderId: Order['id'];
      startDate: Date;
      vesselId: Vessel['id'] | null;
    }>,
    opts: {
      shipWithOrderId: Order['id'] | null;
      voyagePlacement: VoyagePlacement;
    },
  ) => void;
  onCreateCoaAssignment: (
    assignments: Array<{
      orderId: number;
      coaId: number;
      vesselId: number | null;
      startDate: Date;
    }>,
    opts: {
      shipWithOrderId: Order['id'] | null;
      startIsOverride: boolean;
    },
  ) => void;
  onCreateSpotAssignment: (
    assignments: Array<{
      orderId: number;
      startDate: Date;
      ownerOrganizationId: number | null;
      vesselId: number | null;
    }>,
    opts: {
      shipWithOrderId: Order['id'] | null;
      startIsOverride: boolean;
    },
  ) => void;
  onChangeOrdersVoyage: (
    orderIds: Set<Order['id']>,
    targetTimelineId: EventTimeline['id'],
    opts: {
      startDate: Date;
      shipWithOrderId: number | null;
      voyagePlacement: VoyagePlacement;
      isWholeShipment: boolean;
      singleEventIds: string[];
    },
  ) => void;
  onAddOrderToAssignment: (
    targetOrderId: Order['id'],
    newAssignmentOrderIds: Array<Order['id']>,
    startLoad: Date,
  ) => void;
  onRemoveOrderFromShipment: (orderId: Order['id']) => void;
  onMoveSingleEvent: (
    targetTimelineId: EventTimeline['id'],
    singleEventIds: string[],
    opts: { startDate: Date },
  ) => void;
  onSuggestionSelect: (vesselId: number, suggestionIndex: number) => void;
  onDismissSuggestion: (vesselId: number, suggestionIndex: number) => void;
  dismissedSuggestionIds: Set<string>;
  isSuggestAvailable: boolean;
  isCompressedView?: boolean;
  onSuggestVoyage: (vesselId: number, addToShipment?: ClientId) => void;
  onOpenVoyageSuggestionReport: () => void;
  onSwapScheduleEntry: (
    timelineId: EventTimeline['id'],
    indexToMove: number,
    direction: SwapDirection,
    timeDataItem: TimeData,
  ) => void;
  onSwapPortCall?: (
    timelineId: EventTimeline['id'],
    portCallId: ClientId,
    shipmentIds: Array<ScenarioOrder['shipmentId']>,
    direction: SwapDirection,
    step?: number,
  ) => void;
  onViewAssignment: (timelineFocus?: TimelineFocus) => void;
  onDeleteAssignment: (
    orderIds: number[],
    keepShipment: boolean,
    singleEventIds?: string[],
  ) => void;
  onRearrangeShipmentEvents: (orderIds: number[], events: TimeData[]) => void;
  onResetAssignmentsToMaster: (items: ResetAssignmentsToMasterOpts) => void;
  onOrderSelectionChange: (newOrderIds: number[]) => void;
  onEventSelectionChange: (newEventIds: (number | string)[]) => void;
  onSuggestionSelectionChange: (newSuggestionIds: (number | string)[]) => void;
  onPublishOrder?: (ordersId: number, isPublish: boolean) => void;
  onMergeToShipment?: (orderIds: number[]) => void;
  onSaveOffHire: (offHire: OffHireEntry) => void;
  onViewOffHire: (offHire: OffHireEntry) => void;
  onDeleteOffHire: (offHire: OffHireEntry) => void;
  onViewOpenPosition: (
    openPosition: OpenPositionsFragment,
    lastItemEnd?: number,
  ) => void;
  onViewVesselReservation: (
    vesselReservation: VesselReservationFragment,
    lastItemEnd?: number,
  ) => void;
  onSetVoyageAttribute: (
    clientId: ClientId,
    shipment: ScenarioShipment | null,
    hasChange: boolean,
  ) => void;
  onCreateSingleEvent: (
    type: SingleEventType,
    vesselId: number,
    locationId: number,
    locationName: string,
    locationTimeZone: string | null,
    duration: number,
    index: number,
    start: number,
    modalKey?: string,
  ) => void;
  onViewSingleEvent: (
    singleEvent: SingleEvent,
    timeline: EventTimeline,
  ) => void;
  onDeleteSingleEvent: (singleEvent: SingleEvent) => void;
  onToggleGroupCollapse: (
    groupId: EventTimeline['id'],
    isCollapsed: boolean,
  ) => void;
  onToggleShipment?: (
    shipmentId: number | ClientId,
    isCollapsed: boolean,
  ) => void;
  scenario: ScenarioSchedule;
  integrationMasterScenario?: ScenarioSchedule;
  timelineDiff: ScenarioTimelineDiff;
  scheduleContext: Pick<
    ScheduleDataContext,
    'locations' | 'distances' | 'portCalls'
  >;
  suggestions?: SuggestionsByVessel;
  onClearSuggestions: () => void;
  timelines: EventTimeline[];
  completedData?: CompletedData;
  scenarioMenuItems: ScenarioMenuItemType[];
  vessels: ReadonlyArray<Vessel>;
  orders: WithRegionNames<ScenarioOrder>[];
  organizations: Map<number, ScenarioTimeline_OrganizationFragment>;
  selectedOrderIds: Set<number>;
  selectedEventIds: Set<number | string>;
  selectedSuggestionIds: Set<number | string>;
  coas: ReadonlyArray<ScenarioTimeline_CoaFragment>;
  sharedOrderIds: Set<number>;
  // Should be memoized
  filterGroups?: (timeline: EventTimeline) => boolean;
  filterGroupsByRegionBalance?: (timeline: EventTimeline) => boolean;
  regionPolygons: RegionPolygons | undefined;
  regionsByLocation: DefaultMap<LocationData | undefined | null, string | null>;
  setRegionBalanceFilter: (newFilters: RegionBalanceFilter) => void;
  regionBalanceFilter: RegionBalanceFilter;
  charteringOpenPositions: OpenPositionsFragment[];
  vesselReservations: VesselReservationFragment[];
  pnlData?: PnlData;
  isMobileView?: boolean;
  onOptimizeVoyageSpeed?: (params: {
    timelineId: EventTimeline['id'];
    portCallsToOptimize: Set<ClientId>;
  }) => void;
}

export const makeTimelineItemRenderer = ({
  getTimelineOffsetTop,
  locations,
  portCallsById,
  onAddOrderToAssignment,
  onChangeOrdersVoyage,
  scrollToTimeline,
  showNSuggestions = 0,
  onShowMoreSuggestions,
  observer,
  isSuggestAvailable,
  onSuggestVoyage,
  onViewAssignment,
  showInquiries,
  shipmentIntegrationStatus,
  isReadOnlyScenario,
  isMobileView,
  suggestions,
}: {
  getTimelineOffsetTop: (timeline: EventTimeline) => number;
  locations: ScenarioTimelineProps['scheduleContext']['locations'];
  portCallsById?: ScenarioTimelineProps['scheduleContext']['portCalls'];
  onAddOrderToAssignment?: ScenarioTimelineProps['onAddOrderToAssignment'];
  onChangeOrdersVoyage?: ScenarioTimelineProps['onChangeOrdersVoyage'];
  scrollToTimeline: (timeline: EventTimeline, offsetTop?: number) => void;
  showNSuggestions?: number;
  onShowMoreSuggestions?: () => void;
  observer: CustomObserver | undefined;
  isSuggestAvailable?: boolean;
  onSuggestVoyage?: (
    vesselId: number,
    addToShipment?: number | ClientId,
  ) => void;
  onViewAssignment?: (timelineFocus?: TimelineFocus) => void;
  showInquiries: boolean;
  shipmentIntegrationStatus: ShipmentIntegrationStatus;
  isReadOnlyScenario?: boolean;
  isMobileView?: boolean;
  suggestions?: SuggestionsByVessel;
}) => {
  return ({
    item,
    getItemProps,
    getResizeProps,
    itemContext: { dimensions, selected, dragging, pixelsPerMs },
  }: ItemRendererProps<ScenarioTimelineItem> & {
    timelineContext: TimelineContextValue;
  }): React.ReactNode => {
    const borderWidth = 1;
    const { type, isShadow, isCompressedView } = item;

    const isScheduledOrder = type === ItemType.SCHEDULED_ORDER;
    const isReservation = type === ItemType.RESERVATION;
    const isSuggestion = type === ItemType.SUGGESTION;
    const isCompleted = type === ItemType.COMPLETED;
    const isOffHire = type === ItemType.OFFHIRE;
    const isEvent = type === ItemType.EVENT;
    const isOpen = type === ItemType.OPEN;
    const isSpot = type === ItemType.SPOT;
    const isCoa = type === ItemType.COA;
    const isTcIn = type === ItemType.TC_IN;

    const isAssignment = isScheduledOrder || isCoa || isSpot;

    const itemProps = getItemProps({
      style: {
        background: undefined,
        border: undefined,
        borderRightWidth: undefined,
        outlineStyle:
          selected && (isAssignment || isSuggestion || isEvent)
            ? 'solid'
            : 'none',
        outlineWidth: '4px',
        outlineColor: '#4F88E7',
        borderWidth: `${borderWidth}px`,
        borderColor: isScheduledOrder || isCoa ? '#c4cdda' : 'transparent',
        borderStyle: 'solid',
        margin: isCompressedView ? '1px 0' : `${4 - borderWidth}px 0`,
        opacity: isShadow ? 0.5 : 1,
      },
    });
    const styleProps = itemProps.style;
    styleProps.height = isCompressedView
      ? selected
        ? '42px'
        : '25px'
      : // when viewing suggestions in mobile view
        // we set fixed height for offHire items
        // and auto height for all items except suggestions,
        // keep calculated height for suggestion items
        isMobileView && suggestions
        ? isOffHire
          ? '44px'
          : !isSuggestion
            ? 'auto'
            : `${dimensions.height - 8}px`
        : `${dimensions.height - 8}px`;
    if (isCompleted)
      return (
        <div {...itemProps}>
          <TimelineCompletedVoyageRenderer item={item} />
        </div>
      );

    const suggestionIndex = isSuggestion ? item.suggestionIndex : Infinity;

    const isHiddenSuggestion =
      isSuggestion && suggestionIndex > showNSuggestions;

    if (isHiddenSuggestion) {
      return null;
    }

    const isShowMoreSuggestionsButton =
      isSuggestion && suggestionIndex === showNSuggestions;

    const isShownSuggestion =
      isSuggestion && suggestionIndex < showNSuggestions;

    if (isShownSuggestion) {
      // Calculate correct left and width
      // start_time and end_time are min and max of all suggestions in voyage group
      // start and end are actual ones for current suggestion
      const { start_time, start, end } = item;
      const realDuration = end - start;
      const timeOffset = start - start_time;
      styleProps.width = realDuration * pixelsPerMs + 'px';
      styleProps.left = dimensions.left + timeOffset * pixelsPerMs + 'px';
    }

    const { left: leftResizeProps } = getResizeProps();
    const zIndex = leftResizeProps?.style.zIndex;

    const orders = 'orders' in item ? item.orders : [];

    const numOrders =
      'suggestion' in item ? item.suggestion.entries.length : orders.length;

    const isToggleExpanded =
      'isToggleExpanded' in item && item.isToggleExpanded;

    const onToggleShipment =
      'onToggleShipment' in item && item.onToggleShipment;

    const isShipmentToggleVisible =
      onToggleShipment && (numOrders > 1 || isToggleExpanded);

    const originalContract =
      'originalContract' in item ? item.originalContract : '';

    const isLastScheduleItem =
      'isLastScheduleItem' in item && item.isLastScheduleItem;

    const getTestIdSuffix = () => {
      if (isAssignment) {
        return orders.find((order) => order.shipment?.name)?.shipment?.name;
      }
      if (isOffHire) {
        return `offhire-${item.locationAName}-${item.locationBName}`;
      }
      if (isReservation) {
        return `reservation-${item.vesselReservation.shipByVesselId?.name}`;
      }
      if (isSuggestion) {
        return `suggestion-${item.suggestionIndex}`;
      }
      return '';
    };

    const testId = `shipment-box-name-${getTestIdSuffix()}`;

    itemProps.ref = null;
    return (
      <IntersectionObserverBox
        {...itemProps}
        data-testid={testId}
        data-selected={selected}
        observer={observer}
        placeholder={
          isAssignment ? (
            <TimelineVoyagePlaceholder
              isCompressedView={isCompressedView}
              combinedDiff={item.combinedDiff}
              scenarioType={item.scenarioType}
              isShared={item.isShared}
            />
          ) : null
        }
      >
        {isAssignment && (
          <TimelineVoyageRenderer
            item={item}
            groupObj={undefined}
            zIndex={zIndex}
            selected={selected}
            isShadow={isShadow}
            originalContract={originalContract}
            onAddOrderToAssignment={onAddOrderToAssignment}
            getTimelineOffsetTop={getTimelineOffsetTop}
            timeline={item.groupObj.timeline}
            scrollToTimeline={scrollToTimeline}
            onChangeOrdersVoyage={onChangeOrdersVoyage}
            locations={locations}
            portCallsById={portCallsById}
            timelineId={item.groupObj.timeline.id}
            dragging={dragging}
            showInquiries={showInquiries}
            onViewAssignment={onViewAssignment}
            pixelsPerMs={pixelsPerMs}
            shipmentIntegrationStatus={shipmentIntegrationStatus}
          />
        )}
        {isEvent && (
          <TimelineSingleEventRenderer
            item={item}
            zIndex={zIndex}
            selected={selected}
            pixelsPerMs={pixelsPerMs}
          />
        )}
        {isOffHire && (
          <TimelineOffHireEventRenderer
            item={item}
            zIndex={zIndex}
            pixelsPerMs={pixelsPerMs}
          />
        )}
        {isOpen && <TimelineOpenPositionRenderer item={item} />}
        {isReservation && <TimelineVesselReservationRenderer item={item} />}
        {isTcIn && (
          <TimelineTcInEventRenderer
            item={item}
            zIndex={zIndex}
            pixelsPerMs={pixelsPerMs}
          />
        )}
        {isSuggestion &&
          (isShownSuggestion ? (
            <TimelineSuggestionRenderer
              pixelsPerMs={pixelsPerMs}
              item={item}
              isMobileView={isMobileView}
            />
          ) : (
            <Box
              position="absolute"
              borderWidth={1}
              borderStyle="dashed"
              borderColor="neutral.gray_700"
              onClick={onShowMoreSuggestions ?? noop}
              bottom={0}
              right={0}
              left={0}
            >
              <View
                bg={`rgba(242, 244, 246, 1)`}
                borderRadius={4}
                px={3}
                py={2}
                style={suggestionShadowStyles}
                borderWidth={2}
                borderStyle="solid"
                alignItems="center"
              >
                <Text
                  fontWeight="bold"
                  position="sticky"
                  width="fit-content"
                  left="200px"
                  right="0"
                  color="neutral.black_700"
                >
                  Show more suggestions
                </Text>
              </View>
            </Box>
          ))}
        {isShipmentToggleVisible && !isShowMoreSuggestionsButton && (
          <TimelineToggle
            isToggleExpanded={isToggleExpanded}
            onToggleShipment={onToggleShipment}
          />
        )}
        {isSuggestAvailable &&
        onSuggestVoyage &&
        isLastScheduleItem &&
        'groupObj' in item &&
        item.groupObj ? (
          <SuggestButton
            groupObj={item.groupObj}
            leftOffset={styleProps.width}
            onSuggestVoyage={onSuggestVoyage}
            isCompressedView={isCompressedView}
            isReadOnlyScenario={isReadOnlyScenario}
          />
        ) : null}
      </IntersectionObserverBox>
    );
  };
};

const SuggestButton = ({
  groupObj,
  leftOffset,
  onSuggestVoyage,
  isCompressedView,
  isReadOnlyScenario,
}: {
  groupObj: TimelineGroup<ScenarioTimelineGroupFields>;
  leftOffset?: CSSProperties['width'] | undefined;
  onSuggestVoyage: (
    vesselId: number,
    addToShipment?: number | ClientId,
  ) => void;
  isCompressedView: boolean | undefined;
  isReadOnlyScenario?: boolean;
}) => {
  const timeline = groupObj.timeline;
  const vesselId = timeline.vesselId;

  if (
    !vesselId ||
    timeline.type !== 'tc' ||
    groupObj.suggestions.length !== 0 ||
    isReadOnlyScenario
  ) {
    return null;
  }
  return (
    <Box
      position="absolute"
      top={0}
      left={leftOffset}
      width="auto"
      height="30px"
      onTouchEnd={(e) => {
        e.stopPropagation();
      }}
    >
      <Button
        ml={3}
        color="#62666a"
        borderColor="#c9d4df"
        backgroundColor="#eff2f5"
        height={isCompressedView ? '22px' : `32px`}
        onPress={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (vesselId) {
            onSuggestVoyage(vesselId);
          }
        }}
      >
        Suggestions...
      </Button>
    </Box>
  );
};

const TimelineOffHireEventImpl = (props: {
  item: TimelineItem<TimelineOffHireEvent>;
  pixelsPerMs: number;
  zIndex?: CSSProperties['zIndex'] | undefined;
}) => {
  const {
    pixelsPerMs,
    zIndex = 0,
    item: { timeData },
  } = props;

  const {
    hoursAtSea,
    cancellingOrDeliveryViolationVsEta: cancellingViolation,
    physicalTimeConflict,
    hoursWaitingOrRtaBuffer: loadWaiting,
    event,
    start,
  } = timeData;

  if (!isOffHireEvent(event)) {
    throw new Error(`Bad event type for TimelineOffHireEvent component`);
  }

  const { locationAName, locationBName } = event;

  const waitingStart = start - hToMs(loadWaiting);
  const ballastStart = waitingStart - hToMs(hoursAtSea);

  const ballastMs = hToMs(hoursAtSea);
  const waitingMs = hToMs(loadWaiting);
  const cancellingViolationMs = hToMs(cancellingViolation);

  const ballastWidth = ballastMs * pixelsPerMs + 8;
  const waitingWidth = waitingMs * pixelsPerMs;
  const cancellingWidth = cancellingViolationMs * pixelsPerMs;
  const ballastOffset = -(ballastWidth + waitingWidth);
  const waitingOffset = -waitingWidth;
  const cancellingOffset = -cancellingWidth;

  return (
    <Box width="100%" height="100%" display="flex" alignItems="center">
      {ballastMs > 0 ? (
        <TimelineLineIndicator
          tooltip={{
            title: 'Ballast voyage',
            duration: hoursAtSea,
            startTime: ballastStart,
          }}
          width={ballastWidth}
          color="voyageEvents.voyage"
          offset={ballastOffset}
          position="bottom"
          zIndex={zIndex}
        />
      ) : null}
      {waitingMs > 0 ? (
        <TimelineLineIndicator
          tooltip={{
            title: 'Idle',
            duration: loadWaiting,
            startTime: waitingStart,
          }}
          width={waitingWidth}
          offset={waitingOffset}
          color="voyageEvents.idle"
          position="bottom"
          lineStyle="solid"
        />
      ) : null}
      {cancellingViolationMs > 0 ? (
        <TimelineLineIndicator
          tooltip={{
            title: 'Start time violation',
            duration: cancellingViolation,
          }}
          color="voyageEvents.violation"
          width={cancellingWidth}
          offset={cancellingOffset}
        />
      ) : null}
      <View
        width={1}
        height="100%"
        bg="orange.orange_600"
        borderStyle="solid"
        borderWidth={2}
        borderColor={physicalTimeConflict ? 'yellow.yellow_300' : '#c4cdda'}
        justifyContent="center"
        display="inline-block"
      >
        <View
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          py={1}
          overflow="hidden"
          height="100%"
        >
          {[locationAName, locationBName].map((port, index, arr) => (
            <View
              key={index}
              maxWidth={`${100 / arr.length}%`}
              px={2}
              overflow="hidden"
            >
              <Text
                isUnselectable
                isSingleLine
                fontSize={2}
                color="neutral.white_50"
                overflow="hidden"
              >
                {port}
              </Text>
            </View>
          ))}
        </View>
      </View>
    </Box>
  );
};

const TimelineOpenPositionImpl = (props: {
  item: TimelineItem<TimelineOpenPosition>;
}) => {
  const {
    item: {
      openPosition: { locationByStartPortId, locationByDirectionId, remark },
    },
  } = props;
  const startPortName = locationByStartPortId?.name;
  const direction = locationByDirectionId?.name;
  const isRemarkEmpty =
    remark == null || (remark && remark?.trim().length === 0);
  return (
    <Box width="100%" height="100%" display="flex" alignItems="center">
      <View
        width={1}
        height="50%"
        bg="orange.orange_600"
        borderStyle="solid"
        borderWidth={2}
        justifyContent="center"
        display="inline-block"
      >
        <View
          flexDirection="row"
          alignItems="center"
          py={1}
          overflow="hidden"
          height="100%"
        >
          {!isRemarkEmpty && (
            <Tooltip
              content={
                <View>
                  <Text>{remark}</Text>
                </View>
              }
              followCursor="horizontal"
            >
              <Box
                width={13}
                height={13}
                minWidth={13}
                display="flex"
                alignItems="center"
                mr="2px"
              >
                <InfoIcon />
              </Box>
            </Tooltip>
          )}
          <View px={2} overflow="hidden">
            <Text
              isUnselectable
              isSingleLine
              fontSize={2}
              color="neutral.white_50"
              overflow="hidden"
            >
              Open Position:
              {startPortName}
              {direction ? ` -> ${direction}` : ''}
            </Text>
          </View>
        </View>
      </View>
    </Box>
  );
};

const TimelineTcInEventImpl = (props: {
  item: TimelineItem<TimelineTcInEvent>;
  pixelsPerMs: number;
  zIndex?: CSSProperties['zIndex'] | undefined;
}) => {
  const {
    pixelsPerMs,
    zIndex = 0,
    item: { timeData },
  } = props;

  const {
    hoursAtSea,
    cancellingOrDeliveryViolationVsEta: cancellingViolation,
    physicalTimeConflict,
    hoursWaitingOrRtaBuffer: loadWaiting,
    event,
    start,
  } = timeData;

  if (!isStaticEvent(event)) {
    throw new Error(`Bad event type for TimelineTcInEvent component`);
  }

  const {
    locationAName,
    locationATimezone,
    locationBName,
    locationBTimezone,
    entry: { delivery, redelivery },
  } = event;

  const waitingStart = start - hToMs(loadWaiting);
  const ballastStart = waitingStart - hToMs(hoursAtSea);

  const ballastMs = hToMs(hoursAtSea);
  const waitingMs = hToMs(loadWaiting);
  const cancellingViolationMs = hToMs(cancellingViolation);

  const ballastWidth = ballastMs * pixelsPerMs + 8;
  const waitingWidth = waitingMs * pixelsPerMs;
  const cancellingWidth = cancellingViolationMs * pixelsPerMs;
  const ballastOffset = -(ballastWidth + waitingWidth);
  const cancellingOffset = -cancellingWidth;

  return (
    <Box width="100%" height="100%" display="flex" alignItems="center">
      {ballastMs > 0 ? (
        <TimelineLineIndicator
          tooltip={{
            title: 'Ballast voyage',
            duration: hoursAtSea,
            startTime: ballastStart,
          }}
          width={ballastWidth}
          color="voyageEvents.voyage"
          offset={ballastOffset}
          position="bottom"
          zIndex={zIndex}
        />
      ) : null}
      {cancellingViolationMs > 0 ? (
        <TimelineLineIndicator
          tooltip={{
            title: 'Redelivery time violation',
            duration: cancellingViolation,
          }}
          color="voyageEvents.violation"
          width={cancellingWidth}
          offset={cancellingOffset}
        />
      ) : null}
      <View
        width={1}
        height="100%"
        bg="neutral.gray_400"
        borderStyle="solid"
        borderWidth={2}
        borderColor={physicalTimeConflict ? 'yellow.yellow_300' : '#c4cdda'}
        justifyContent="center"
        display="inline-block"
      >
        <View
          flexDirection="column"
          justifyContent="space-between"
          alignItems="flex-start"
          py={1}
          overflow="hidden"
          height="100%"
        >
          <View
            width="100%"
            px={2}
            overflow="hidden"
            flexDirection="row"
            justifyContent="space-between"
          >
            <Text
              isUnselectable
              isSingleLine
              fontSize={2}
              color="neutral.white_50"
              overflow="hidden"
            >
              {redelivery ? (
                <Tooltip
                  content={
                    <Box>
                      <strong>TC In redelivery</strong>
                      <br />
                      <strong>Redelivery port:</strong> {locationAName}
                      <br />
                      <strong>Earliest redelivery date:</strong>{' '}
                      <TimeField
                        value={redelivery.startDate}
                        timeZone={locationATimezone}
                      />
                      <br />
                      <strong>Latest redelivery date:</strong>{' '}
                      <TimeField
                        value={redelivery.endDate}
                        timeZone={locationATimezone}
                      />
                      <br />
                      <strong>TC In External ID:</strong>{' '}
                      {redelivery.tcContractExternalId}
                    </Box>
                  }
                >
                  <Text>{locationAName}</Text>
                </Tooltip>
              ) : null}
            </Text>
            <Text
              isUnselectable
              isSingleLine
              fontSize={2}
              color="neutral.white_50"
              overflow="hidden"
            >
              {delivery ? (
                <Tooltip
                  content={
                    <Box>
                      <strong>TC In delivery</strong>
                      <br />
                      <strong>Delivery port:</strong> {locationBName}
                      <br />
                      <strong>Earliest delivery date:</strong>{' '}
                      <TimeField
                        value={delivery.startDate}
                        timeZone={locationBTimezone}
                      />
                      <br />
                      <strong>Latest delivery date:</strong>{' '}
                      <TimeField
                        value={delivery.endDate}
                        timeZone={locationBTimezone}
                      />
                      <br />
                      <strong>TC In External ID:</strong>{' '}
                      {delivery.tcContractExternalId}
                    </Box>
                  }
                >
                  <Text>{locationBName}</Text>
                </Tooltip>
              ) : null}
            </Text>
          </View>
          <View
            width="100%"
            px={2}
            overflow="hidden"
            flexDirection="row"
            justifyContent="space-between"
          >
            <Text
              isUnselectable
              isSingleLine
              fontSize={2}
              color="neutral.white_50"
              overflow="hidden"
            >
              {redelivery ? 'Redelivery' : null}
            </Text>
            <Text
              isUnselectable
              isSingleLine
              fontSize={2}
              color="neutral.white_50"
              overflow="hidden"
            >
              {delivery ? 'Delivery' : null}
            </Text>
          </View>
        </View>
      </View>
    </Box>
  );
};

const TimelineVesselReservationImpl = (props: {
  item: TimelineItem<TimelineVesselReservation>;
}) => {
  const {
    item: {
      isCompressedView,
      vesselReservation: {
        locationByStartPortId,
        locationByEndPortId,
        regionByStartRegionId,
        regionByEndRegionId,
        userByCreatedByUserId,
        expireTime,
        remark,
        type,
      },
    },
  } = props;

  const startPort = locationByStartPortId?.name;
  const endPort = locationByEndPortId?.name;
  const startRegion = regionByStartRegionId?.name;
  const endRegion = regionByEndRegionId?.name;
  const createdByUser = userByCreatedByUserId?.name?.split('@').at(0);
  const vType = vesselReservationTypes.find((i) => i.id === type);
  const isRemarkEmpty = remark?.trim().length === 0;
  const isExpired = !!expireTime && new Date(expireTime).getTime() < Date.now();

  return (
    <UnselectableDiv style={fullSize}>
      <View
        width={1}
        height="100%"
        bg="neutral.white_50"
        borderWidth={1}
        borderColor="neutral.gray_225"
      >
        <Box height="100%" display="flex" flexDirection="column">
          <View
            flexDirection="row"
            bg={isExpired ? 'voyageEvents.violation' : 'orange.orange_500'}
            overflow="hidden"
            height="20px"
            alignItems="center"
            justifyContent="space-between"
          >
            <>
              <PortNameIndicator portName={startPort ?? startRegion} />
              <PortNameIndicator portName={endPort ?? endRegion} />
            </>
          </View>
          {!isCompressedView && (
            <Box
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              px={2}
              height="18px"
            >
              <View
                height="100%"
                width={1}
                overflow="hidden"
                flexDirection="row"
                alignItems="center"
              >
                {!isRemarkEmpty && (
                  <Tooltip
                    content={
                      <View>
                        <Text>{remark}</Text>
                      </View>
                    }
                    followCursor="horizontal"
                  >
                    <Box
                      width={13}
                      height={13}
                      minWidth={13}
                      display="flex"
                      alignItems="center"
                      mr="2px"
                    >
                      <NoteIcon />
                    </Box>
                  </Tooltip>
                )}
                <Text color="neutral.black_700" fontSize={1} isSingleLine>
                  {vType?.label} ({createdByUser})
                </Text>
              </View>
            </Box>
          )}
        </Box>
      </View>
    </UnselectableDiv>
  );
};

const TimelineSingleEventImpl = (props: {
  item: TimelineItem<TimelineEvent>;
  pixelsPerMs: number;
  zIndex?: CSSProperties['zIndex'] | undefined;
  selected: boolean | undefined;
}) => {
  const {
    selected,
    pixelsPerMs,
    zIndex = 0,
    item: { id, timeData, start_time, end_time, portCallEvents },
  } = props;
  const {
    hoursAtSea,
    cancellingOrDeliveryViolationVsEta: cancellingViolation,
    physicalTimeConflict,
    event,
    event: { locationName, locationTimeZone },
  } = timeData;

  if (!isSingleEvent(event)) {
    throw new Error(`Bad event type for TimelineSingleEvent component`);
  }

  const arrival = portCallEvents?.Arrive;
  const arrivalStart = arrival?.start ?? 0;
  const arrivalDurationMs = arrival?.duration ?? 0;

  const cancellingViolationMs = hToMs(cancellingViolation);
  const ballastStart = arrivalStart - hToMs(hoursAtSea);
  const ballastDurationMs = hToMs(hoursAtSea);
  const durationMs = end_time - start_time;

  const ballastWidth = ballastDurationMs * pixelsPerMs + 3;
  const arrivalWidth = arrivalDurationMs * pixelsPerMs;
  const cancellingWidth = cancellingViolationMs * pixelsPerMs;
  const ballastOffset = -(ballastWidth + arrivalWidth);
  const arrivalOffset = -arrivalWidth;
  const cancellingOffset = -cancellingWidth;

  const clickHandlers = useSingleClickHandlers();

  return (
    <Box
      width="100%"
      height="100%"
      display="flex"
      alignItems="center"
      draggable={selected}
      onDragStart={(e) => {
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          return;
        }
        setDragData(e, {
          type: 'single_event',
          orderIds: [],
          singleEventId: id,
        });
      }}
      onDragEnd={() => {
        setDropEffect('none', '', true);
      }}
      {...clickHandlers}
    >
      {ballastDurationMs > 0 ? (
        <TimelineLineIndicator
          tooltip={{
            title: 'Ballast voyage',
            duration: hoursAtSea,
            startTime: ballastStart,
          }}
          width={ballastWidth}
          color="voyageEvents.voyage"
          offset={ballastOffset}
          position="bottom"
          zIndex={zIndex}
        />
      ) : null}
      {arrivalDurationMs > 0 ? (
        <TimelineLineIndicator
          tooltip={{
            title: `Single event at ${locationName}`,
            duration: msToH(arrivalDurationMs),
            startTime: arrivalStart,
            timezone: locationTimeZone ?? undefined,
          }}
          width={arrivalWidth}
          offset={arrivalOffset}
          color="voyageEvents.portCall"
          position="bottom"
          lineStyle="dashed"
        />
      ) : null}
      {cancellingViolationMs > 0 ? (
        <TimelineLineIndicator
          tooltip={{
            title: 'Start time violation',
            duration: cancellingViolation,
          }}
          color="voyageEvents.violation"
          width={cancellingWidth}
          offset={cancellingOffset}
        />
      ) : null}
      <Tooltip
        content={
          <div>
            <Text>
              Single event at {locationName}:{' '}
              {singleEventTypes[event.event].label}
            </Text>
            <br />
            <Text color="neutral.gray_430">
              Start time:{' '}
              {timeFieldFormat(start_time, locationTimeZone ?? undefined)}
            </Text>
            <br />
            <Text color="neutral.gray_430">
              Total Duration: {formatHToDaysHour(msToH(durationMs))}
            </Text>
          </div>
        }
        followCursor="horizontal"
      >
        <View
          width={1}
          height="100%"
          bg="orange.orange_600"
          borderStyle="solid"
          borderColor={physicalTimeConflict ? 'yellow' : 'neutral.gray_225'}
          justifyContent="center"
          display="inline-block"
        >
          <View
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            py={1}
            overflow="hidden"
            height="50%"
          >
            <View maxWidth="100%" px={2} overflow="hidden">
              <Text
                isUnselectable
                isSingleLine
                fontSize={2}
                color="neutral.white_50"
                overflow="hidden"
              >
                {locationName}
              </Text>
            </View>
          </View>
          <View
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            py={1}
            overflow="hidden"
            height="50%"
          >
            <View maxWidth="100%" px={2} overflow="hidden">
              <Text
                isUnselectable
                isSingleLine
                fontSize={2}
                color="neutral.white_50"
                overflow="hidden"
              >
                {singleEventTypes[event.event].label}
              </Text>
            </View>
          </View>
        </View>
      </Tooltip>
    </Box>
  );
};

const TimelineToggleImpl = (props: {
  isToggleExpanded: boolean;
  onToggleShipment: (isCollapsed: boolean) => void;
}) => {
  const { isToggleExpanded, onToggleShipment } = props;

  return (
    <Tooltip content={<Text>View orders on timeline</Text>}>
      <Box
        position="absolute"
        bottom="-7px"
        color="#000"
        left="calc(50% - 10px)"
        bg="neutral.white_100"
        width={20}
        height={20}
        borderWidth={1}
        borderStyle="solid"
        borderColor="#607694"
        borderRadius={10}
        backgroundColor="#F1F3F6"
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
        onClick={(e) => {
          onToggleShipment(!isToggleExpanded);
          e.stopPropagation();
        }}
      >
        <Box
          width="100%"
          height="100%"
          fontSize={3}
          textAlign="center"
          fontWeight="bold"
          lineHeight="1em"
          style={{
            transform: !isToggleExpanded ? '' : 'rotate(180deg)',
            color: isToggleExpanded ? 'orange' : undefined,
          }}
        >
          ▾
        </Box>
      </Box>
    </Tooltip>
  );
};

const TimelineToggle = React.memo(TimelineToggleImpl);
const TimelineOffHireEventRenderer = React.memo(TimelineOffHireEventImpl);
const TimelineOpenPositionRenderer = React.memo(TimelineOpenPositionImpl);
const TimelineSingleEventRenderer = React.memo(TimelineSingleEventImpl);
const TimelineVesselReservationRenderer = React.memo(
  TimelineVesselReservationImpl,
);
const TimelineTcInEventRenderer = React.memo(TimelineTcInEventImpl);

export interface ScenarioTimelineRef {
  scrollToToday: () => void;
  collapseExpandedVoyages: () => void;
}

export type DropOrdersToTimeline = z.infer<typeof dropOrdersToTimelineSchema>;

export type DragDropData = z.infer<typeof dragDropDataSchema>;

export const getDroppedOrderIds = (item: DropOrdersToTimeline): number[] => {
  const { selectedOrderIds, dragOrderId } = item;

  assertDefined(dragOrderId, `Expected to be dragging an order`);

  return selectedOrderIds.includes(dragOrderId)
    ? selectedOrderIds
    : [dragOrderId];
};

function FocusedOrdersListener({
  focusToOrders,
}: {
  focusToOrders: (orderIds: number[]) => void;
}): null {
  const [focusedOrders, setFocusedOrders] = useFocusedOrders();

  useEffect(() => {
    if (focusedOrders !== null) {
      focusToOrders(focusedOrders);
      setFocusedOrders(null);
    }
  }, [focusedOrders, focusToOrders, setFocusedOrders]);

  return null;
}

export const ItemContextMenu = ({
  contextItemId,
  contextItem,
  expandedShipmentIds,
  shouldShowSuggestMoreCargo,
  selectedItems,
  selectedItemsOrders,
  canResetContextOrder,
  clientId,
  scenario,
  handleViewItemDetails,
  hideItemContextMenu,
  setExpandedShipmentIds,
  onRemoveOrderFromShipment,
  onSuggestVoyage,
  onPublishOrder,
  onMergeToShipment,
  onRearrangeShipmentEvents,
  handleDeleteItem,
  handleResetItemToMaster,
  onSetVoyageAttribute,
  onOptimizeVoyageSpeed,
}: {
  contextItemId: ScenarioTimelineItem['id'] | null;
  contextItem: ScenarioTimelineItem;
  expandedShipmentIds: Set<number | ClientId>;
  shouldShowSuggestMoreCargo?: boolean;
  selectedItems: (string | number)[];
  selectedItemsOrders: ScenarioOrder[];
  canResetContextOrder: boolean;
  clientId: ClientId | null | undefined;
  scenario: ScenarioSchedule;
  handleViewItemDetails: (itemId: ScenarioTimelineItem['id']) => void;
  hideItemContextMenu: () => void;
  setExpandedShipmentIds: React.Dispatch<
    React.SetStateAction<Set<number | ClientId>>
  >;
  onRemoveOrderFromShipment: (orderId: Order['id']) => void;
  onSuggestVoyage: (vesselId: number, addToShipment?: ClientId) => void;
  onPublishOrder?: (ordersId: number, isPublish: boolean) => void;
  onMergeToShipment?: (orderIds: number[]) => void;
  onRearrangeShipmentEvents: (orderIds: number[], events: TimeData[]) => void;
  handleDeleteItem: (itemId: ScenarioTimelineItem['id']) => void;
  handleResetItemToMaster: (
    itemId: ScenarioTimelineItem['id'],
    itemType: 'order' | 'shipment',
  ) => void;
  onSetVoyageAttribute: (
    clientId: ClientId,
    shipment: ScenarioShipment | null,
    hasChange: boolean,
  ) => void;
  onOptimizeVoyageSpeed?: () => void;
}) => {
  return (
    <Menu hasShadow>
      <MenuItem
        label="View details"
        onPress={() => {
          if (contextItemId !== null) {
            handleViewItemDetails(contextItemId);
            hideItemContextMenu();
          }
        }}
      />
      {'orders' in contextItem && contextItem.orders[0]?.shipmentId != null && (
        <>
          {expandedShipmentIds.has(contextItem.orders[0].shipmentId) ? (
            <>
              <MenuItem
                label="Collapse shipment"
                onPress={() => {
                  setExpandedShipmentIds((curExpandedShipmentIds) => {
                    if (contextItem.orders[0]?.shipmentId == null) {
                      console.error(
                        'Tried to collapse shipment, but no shipmentId found',
                        contextItem,
                      );
                      return curExpandedShipmentIds;
                    }

                    const s = new Set(curExpandedShipmentIds);
                    s.delete(contextItem.orders[0].shipmentId);
                    return s;
                  });
                  hideItemContextMenu();
                }}
              />

              <MenuItem
                label="Remove order from shipment"
                onPress={() => {
                  onRemoveOrderFromShipment(contextItem.orders[0].id);
                  hideItemContextMenu();
                }}
              />
            </>
          ) : (
            <MenuItem
              label="Expand shipment"
              onPress={() => {
                setExpandedShipmentIds((curCollapsedShipmentIds) => {
                  if (contextItem.orders[0]?.shipmentId == null) {
                    console.error(
                      'Tried to expand shipment, but no shipmentId found',
                      contextItem,
                    );
                    return curCollapsedShipmentIds;
                  }

                  const s = new Set(curCollapsedShipmentIds);
                  s.add(contextItem.orders[0].shipmentId);
                  return s;
                });
                hideItemContextMenu();
              }}
            />
          )}
          {shouldShowSuggestMoreCargo &&
            'events' in contextItem &&
            contextItem.orders[0].shipmentId !== null && (
              <MenuItem
                label="Suggest more cargo"
                onPress={() => {
                  const { shipmentId } = contextItem.orders[0];
                  const { vesselId } = contextItem.events[0].event;

                  if (
                    shipmentId !== null &&
                    shipmentId !== undefined &&
                    vesselId !== null
                  ) {
                    onSuggestVoyage(vesselId, shipmentId);
                  }
                  hideItemContextMenu();
                }}
              />
            )}
          {contextItem.combinedDiff?.isCommitted && onPublishOrder && (
            <MenuItem
              label={
                contextItem.isShared
                  ? 'Unpublish assignment'
                  : 'Publish assignment'
              }
              onPress={() => {
                onPublishOrder(contextItem.orders[0].id, !contextItem.isShared);
                hideItemContextMenu();
              }}
            />
          )}
          {onMergeToShipment &&
            selectedItems.length > 1 &&
            selectedItemsOrders.length > 1 && (
              <MenuItem
                label={'Merge orders into shipment'}
                onPress={() => {
                  onMergeToShipment(
                    selectedItemsOrders.map((order) => order.id),
                  );
                  hideItemContextMenu();
                }}
              />
            )}
          {contextItem.orders.length > 1 &&
            contextItem.type === ItemType.SCHEDULED_ORDER && (
              <MenuItem
                label="Recalculate port rotation"
                onPress={() => {
                  onRearrangeShipmentEvents(
                    contextItem.orders.map((o) => o.id),
                    contextItem.events,
                  );
                  hideItemContextMenu();
                }}
              />
            )}
        </>
      )}
      <MenuItem
        disabled={'orders' in contextItem && contextItem.isShared}
        label="Release assignment (⌫)"
        onPress={() => {
          if (contextItemId !== null) {
            handleDeleteItem(contextItemId);
            hideItemContextMenu();
          }
        }}
      />
      {!('isCollapsed' in contextItem && contextItem.isCollapsed) &&
        canResetContextOrder && (
          <MenuItem
            label="Use committed version (order)"
            onPress={() => {
              if (contextItemId !== null) {
                handleResetItemToMaster(contextItemId, 'order');
              }
              hideItemContextMenu();
            }}
          />
        )}
      {
        <MenuItem
          label="Use committed version (whole shipment)"
          onPress={() => {
            if (contextItemId !== null) {
              handleResetItemToMaster(contextItemId, 'shipment');
            }
            hideItemContextMenu();
          }}
        />
      }
      {clientId && (
        <MenuItem
          label="Set voyage attribute"
          disabled={scenario.type === 'master'}
          onPress={() => {
            const hasChange =
              ('combinedDiff' in contextItem &&
                contextItem.combinedDiff?.hasChange) ??
              false;
            const shipment =
              contextItem && 'events' in contextItem
                ? (contextItem.orders?.at(0)?.shipment ?? null)
                : null;
            onSetVoyageAttribute(clientId, shipment, hasChange);
            hideItemContextMenu();
          }}
        />
      )}
      {'events' in contextItem &&
        contextItem.events.length > 0 &&
        onOptimizeVoyageSpeed && (
          <MenuItem
            label="Optimize vessel speed"
            disabled={scenario.isReadOnly}
            onPress={() => {
              onOptimizeVoyageSpeed();
              hideItemContextMenu();
            }}
          />
        )}
    </Menu>
  );
};

const ScenarioTimeline = forwardRef<ScenarioTimelineRef, ScenarioTimelineProps>(
  (
    {
      onSwapScheduleEntry,
      onSwapPortCall,
      onCreateAssignment,
      onCreateCoaAssignment,
      onCreateSpotAssignment,
      onChangeOrdersVoyage,
      onMoveSingleEvent,
      onViewAssignment,
      onDeleteAssignment,
      onResetAssignmentsToMaster,
      onOrderSelectionChange,
      onEventSelectionChange,
      onSuggestionSelectionChange,
      onSuggestionSelect,
      onDismissSuggestion,
      dismissedSuggestionIds,
      isSuggestAvailable,
      isCompressedView,
      onSuggestVoyage,
      onSaveOffHire,
      onViewOffHire,
      onDeleteOffHire,
      onViewOpenPosition,
      onViewVesselReservation,
      onSetVoyageAttribute,
      onOpenVoyageSuggestionReport,
      onCreateSingleEvent,
      onViewSingleEvent,
      onDeleteSingleEvent,
      selectedOrderIds,
      selectedEventIds,
      selectedSuggestionIds,
      onAddOrderToAssignment,
      onRemoveOrderFromShipment,
      onToggleGroupCollapse,
      onPublishOrder,
      onMergeToShipment,
      onRearrangeShipmentEvents,
      onOptimizeVoyageSpeed,
      timelines,
      scenario,
      timelineDiff,
      suggestions,
      onClearSuggestions,
      orders,
      organizations,
      scheduleContext,
      sharedOrderIds,
      filterGroups,
      filterGroupsByRegionBalance,
      regionPolygons,
      regionsByLocation,
      setRegionBalanceFilter,
      regionBalanceFilter,
      charteringOpenPositions,
      vesselReservations,
      pnlData,
      completedData,
      integrationMasterScenario,
      isMobileView,
    }: ScenarioTimelineProps,
    ref,
  ): JSX.Element => {
    const currentUser = useCurrentUser();
    const euEtsCostEnabled = isEuEtsCostEnabledForUser(currentUser);
    const fuelEuCostEnabled =
      !!currentUser?.rootOrganization?.configIncludeFuelEuInCosts;
    const currentScenarioPnl = useCurrentScenarioPnl();

    const [timelineScroller, setTimelineScroller] = useState<HTMLDivElement>();
    const [observer, setObserver] = useState<CustomObserver | undefined>();

    const timelineRef =
      useRef<ITimelineView<ScenarioTimelineItem, ScenarioTimelineGroup>>(null);
    const timelineScrollRef = (ref: HTMLDivElement) => {
      setTimelineScroller(ref);
      prepareIntersectionObserver(observer, ref, setObserver);
    };

    const shouldShowSuggestMoreCargo = hasFeature('suggest_voyage');
    const isWaitLaycanDefault = useAtomValue(isWaitLaycanDefaultAtom);

    // Item that context menu was opened on
    const [contextItemId, setContextItemId] = useState<
      ScenarioTimelineItem['id'] | null
    >(null);
    const {
      onContextMenu: onItemContextMenu,
      styles: itemMenuStyles,
      isVisible: isItemMenuVisible,
      hideMenu: hideItemContextMenu,
      setMenuRef: setItemMenuRef,
      getButtonProps: getItemMenuButtonProps,
    } = useContextMenu();

    const {
      onContextMenu: onOffHireContextMenu,
      styles: offHireMenuStyles,
      isVisible: isOffHireMenuVisible,
      hideMenu: hideOffHireMenu,
      setMenuRef: setOffHireMenuRef,
    } = useContextMenu();

    const {
      onContextMenu: onOpenPositionContextMenu,
      styles: openPositionMenuStyles,
      isVisible: isOpenPositionMenuVisible,
      hideMenu: hideOpenPositionMenu,
      setMenuRef: setOpenPositionMenuRef,
    } = useContextMenu();

    const {
      onContextMenu: onSingleEventContextMenu,
      styles: singleEventMenuStyles,
      isVisible: isSingleEventMenuVisible,
      hideMenu: hideSingleEventMenu,
      setMenuRef: setSingleEventMenuRef,
    } = useContextMenu();

    const {
      onContextMenu: onVesselReservationContextMenu,
      styles: vesselReservationMenuStyles,
      isVisible: isVesselReservationVisible,
      hideMenu: hideVesselReservation,
      setMenuRef: setVesselReservationRef,
    } = useContextMenu();

    const {
      onContextMenu: onCanvasContextMenu,
      styles: canvasMenuStyles,
      isVisible: isCanvasMenuVisible,
      hideMenu: hideCanvasMenu,
      setMenuRef: setCanvasMenuRef,
    } = useContextMenu();

    const {
      onContextMenu: onVesselContextMenu,
      styles: vesselMenuStyles,
      isVisible: isVesselMenuVisible,
      setMenuRef: setVesselMenuRef,
    } = useContextMenu();

    const [expandedShipmentIds, setExpandedShipmentIds] = useState<
      Set<number | ClientId>
    >(new Set());

    const collapseExpandedVoyages = useCallback(() => {
      setExpandedShipmentIds(new Set());
    }, []);

    const [currentVesselId, setCurrentVesselId] = useState<number | null>(null);
    const currentVesselInFleet = useMemo(() => {
      const vessel = timelines.find(
        (t) => t.vesselId === currentVesselId,
      )?.vessel;
      if (!vessel) {
        return;
      }
      const vesselName = vessel.name;
      const isVesselInFleet = !vessel?.externalFleet;
      return { vesselName, isVesselInFleet };
    }, [timelines, currentVesselId]);
    const [contextTime, setContextTime] = useState<number | null>(null);

    const ordersByShipment = useMemo(
      () => mapGroupBy(orders, (o) => o.shipmentId),
      [orders],
    );
    const integrationOrderIdsByShipmentClientId =
      integrationMasterScenario?.integrationData?.orderIdsByShipmentClientId;

    const shipmentIntegrationStatus: ShipmentIntegrationStatus = useMemo(() => {
      if (!integrationOrderIdsByShipmentClientId) return null;

      const expectedOrderIdsByShipment = new Map<
        ScenarioOrder['shipmentId'],
        Set<number>
      >();
      for (const [sCID, orderIds] of Object.entries(
        integrationOrderIdsByShipmentClientId,
      )) {
        expectedOrderIdsByShipment.set(parseClientId(sCID), new Set(orderIds));
      }

      const actualOrderIdsByShipment = new Map<
        ScenarioOrder['shipmentId'],
        Set<number>
      >();

      const missingOrderIdsByShipment = new DefaultMap<
        ScenarioOrder['shipmentId'],
        number[]
      >(() => []);
      const extraOrderIdsByShipment = new DefaultMap<
        ScenarioOrder['shipmentId'],
        number[]
      >(() => []);

      const scenarioDiffersFromIntegration = new Set<
        ScenarioOrder['shipmentId']
      >();

      for (const [sCID, orders] of ordersByShipment.entries()) {
        if (typeof sCID !== 'string') {
          continue;
        }
        const ids = new Set(orders.map((o) => o.id));
        const clientId = parseClientId(sCID);
        actualOrderIdsByShipment.set(clientId, ids);

        const expected = expectedOrderIdsByShipment.get(clientId);
        if (!expected) {
          continue;
        }

        for (const id of ids) {
          if (!expected.has(id)) {
            extraOrderIdsByShipment.get(clientId).push(id);
            scenarioDiffersFromIntegration.add(clientId);
          }
        }

        for (const id of expected) {
          if (!ids.has(id)) {
            missingOrderIdsByShipment.get(sCID).push(id);
            scenarioDiffersFromIntegration.add(sCID);
          }
        }
      }

      return {
        expectedOrderIdsByShipment,
        actualOrderIdsByShipment,
        missingOrderIdsByShipment,
        extraOrderIdsByShipment,
        scenarioDiffersFromIntegration,
      };
    }, [integrationOrderIdsByShipmentClientId, ordersByShipment]);
    const isReadOnlyScenario = scenario.isReadOnly;

    const orderById = useMemo(() => mapKeyBy(orders, (o) => o.id), [orders]);

    const changeOrdersVoyage: ScenarioTimelineProps['onChangeOrdersVoyage'] =
      useCallback(
        (orderIds, targetTimelineId, opts) => {
          // If at most 1 order is left in a shipment, automatically collapse it

          const shipmentsToCollapse: Array<number | ClientId> = [];
          for (const orderId of orderIds) {
            const order = assertedDefined(
              orderById.get(orderId),
              `expected to find order ${orderId} in timeline`,
            );
            const ordersInShipment =
              ordersByShipment.get(order.shipmentId) ?? [];
            const movedFromShipment = countWith(ordersInShipment, (o) =>
              orderIds.has(o.id),
            );
            if (ordersInShipment.length - movedFromShipment <= 1) {
              shipmentsToCollapse.push(order.shipmentId ?? NaN);
            }
          }

          onChangeOrdersVoyage(orderIds, targetTimelineId, opts);
          if (shipmentsToCollapse.length > 0) {
            setExpandedShipmentIds(
              (prev) =>
                new Set(
                  [...prev].filter(
                    (expandedId) => !shipmentsToCollapse.includes(expandedId),
                  ),
                ),
            );
          }
        },
        [onChangeOrdersVoyage, orderById, ordersByShipment],
      );

    // Options when right click on vessel on timeline
    const [showVesselPrompt, setShowVesselPrompt] = useState({
      deactivateVessel: false,
      modifyVesselToFleet: false,
    });

    const [showRegions, setShowRegions] = useState(false);

    const regions = useMemo(() => {
      const regions = regionPolygons || [];
      return [...regions, { properties: { name: 'Others' } }];
    }, [regionPolygons]);

    const { spotAssignments } = scenario;

    const [isVesselCapacityVisible] = useIsVesselCapacityVisibleState();
    const [isVesselRemarksVisible] = useIsVesselRemarksVisibleState();

    const groupRenderer = useCallback(
      ({
        group,
      }: ReactCalendarGroupRendererProps<ScenarioTimelineGroup>): React.ReactNode => {
        const borderColor =
          group.timeline.type === 'coa' && group.timeline.vesselId == null
            ? 'neutral.gray_250'
            : 'transparent';

        const { isCollapsed, canCollapse, vessel } = group.timeline;
        const capacities = vessel ? getVesselCapacities(vessel) : null;
        const massCapacityLabel = capacities
          ? `/ ${capacities.massCapacitySource === 'DWT' ? 'DWT ' : ''}${
              capacities.massCapacity
            } mts`
          : '';

        // if the length of the remarks is long subtract it with ellipsis
        const rawRemarks = group.timeline.vessel?.remarks ?? '';
        const remarks =
          rawRemarks?.length > 18
            ? `${rawRemarks?.substring(0, 18)} (...)`
            : rawRemarks;

        // if the length of the title is long subtract it with ellipsis unless it's the Undefined contract
        const rawTitle = (group.title ?? '') as string;
        const title =
          ['Relet TBN', 'Undefined spot contract'].includes(rawTitle) ||
          rawTitle.length <= 20
            ? rawTitle
            : `${rawTitle.substring(0, 20)}...`;

        return (
          <Box
            onClick={() => onViewAssignment({ timelineId: group.timeline.id })}
            justifyContent="center"
            height="100%"
            display="flex"
            flexDirection="row"
            justifyItems="left"
            paddingRight="10px"
            alignItems="center"
            onContextMenu={(e) => {
              e.preventDefault();
              if (group.timeline.type === 'tc') {
                setCurrentVesselId(group.timeline.vesselId);
                onVesselContextMenu(e);
              }
            }}
          >
            <Box
              display="flex"
              flexDirection="column"
              flex="1"
              px={3}
              borderLeftStyle="solid"
              borderLeftWidth="2px"
              borderLeftColor={borderColor}
              data-testid={`group-title-${group.title}`}
            >
              <Text
                fontWeight="bold"
                color="neutral.black_700"
                isSingleLine={!isMobileView}
                fontSize={isMobileView ? '1' : '2'}
              >
                {title}
                {group.timeline.type === 'tc' && (
                  <ImosTcContractsDebug
                    vessel={group.timeline.vessel}
                    locationById={scheduleContext.locations}
                  />
                )}
              </Text>
              {!isCompressedView && isVesselCapacityVisible ? (
                <Text
                  fontSize="0"
                  color="neutral.black_700"
                  isSingleLine={!isMobileView}
                >
                  {group.timeline.subtitle} {massCapacityLabel}
                </Text>
              ) : null}

              {!isCompressedView && isVesselRemarksVisible ? (
                <Tooltip
                  disabled={rawRemarks?.length < 18}
                  content={
                    <>
                      <strong>Vessel remarks:</strong> {rawRemarks}
                    </>
                  }
                  followCursor="horizontal"
                >
                  <Text fontSize="1" color="neutral.black_700" isSingleLine>
                    {remarks}
                  </Text>
                </Tooltip>
              ) : null}
            </Box>
            {canCollapse && (
              <>
                <Box
                  display="flex"
                  width="20px"
                  height="20px"
                  marginLeft="4px"
                  justifyContent="center"
                  alignItems="center"
                  borderStyle="solid"
                  borderWidth="1px"
                  borderColor="#607694"
                  borderRadius="10px"
                  backgroundColor="#F1F3F6"
                  color="#000"
                  style={{
                    transform: isCollapsed ? '' : 'rotate(180deg)',
                  }}
                  onClick={(e) => {
                    onToggleGroupCollapse(group.timeline.id, !isCollapsed);
                    e.stopPropagation();
                  }}
                >
                  ▾
                </Box>
              </>
            )}
          </Box>
        );
      },
      [
        isMobileView,
        scheduleContext.locations,
        isCompressedView,
        isVesselCapacityVisible,
        isVesselRemarksVisible,
        onViewAssignment,
        onVesselContextMenu,
        onToggleGroupCollapse,
      ],
    );

    const groups = useMemo((): TimelineGroup<ScenarioTimelineGroupFields>[] => {
      return timelines.map((t) => {
        return {
          id: t.id,
          title: t.title,
          timeline: t,
          stackItems: true,
          suggestions:
            t.vesselId !== null ? (suggestions?.get(t.vesselId) ?? []) : [],
        };
      });
    }, [suggestions, timelines]);

    const filteredGroupsIgnoringRegionBalance = useMemo(() => {
      if (suggestions) {
        const bestPerVessel = new Map<number, Suggestion>();
        // Get objective from any suggestion on any vessel
        const objective = [...suggestions.values()]
          .find((ss) => ss.length > 0)
          ?.at(0)?.objective;
        if (suggestions.size > 1 && objective !== undefined) {
          for (const [vesselId, vesselSuggestions] of suggestions) {
            const firstSuggestion = vesselSuggestions.at(0);
            if (firstSuggestion) {
              bestPerVessel.set(vesselId, firstSuggestion);
            }
          }

          const sortFn: (a: Suggestion, b: Suggestion) => number =
            objective === Objective.MinimizeCost
              ? (a, b) => a.cost - b.cost
              : objective === Objective.MaximizeVCP
                ? (a, b) =>
                    +b.vesselPerformanceMetrics.delta.vcp -
                    +a.vesselPerformanceMetrics.delta.vcp
                : objective === Objective.ShortestDistance
                  ? (a, b) => a.distanceDelta - b.distanceDelta
                  : objective === Objective.ShortestBallast
                    ? (a, b) => a.ballastDelta - b.ballastDelta
                    : (a, b) => b.tce - a.tce;

          return groups
            .filter((g) => !filterGroups || filterGroups(g.timeline))
            .filter(
              (
                g,
              ): g is TimelineGroupBase &
                ScenarioTimelineGroupFields & {
                  timeline: { vesselId: number };
                } =>
                !!g.timeline.vesselId && bestPerVessel.has(g.timeline.vesselId),
            )
            .sort((as, bs) => {
              const a = bestPerVessel.get(as.timeline.vesselId);
              const b = bestPerVessel.get(bs.timeline.vesselId);
              if (!a || !b) return 0;
              return sortFn(a, b);
            });
        }
      }
      return sortTimelineGroups(
        !filterGroups ? groups : groups.filter((g) => filterGroups(g.timeline)),
      );
    }, [groups, filterGroups, suggestions]);

    const filteredGroups = useMemo(() => {
      return filterGroupsByRegionBalance
        ? filteredGroupsIgnoringRegionBalance.filter((g) =>
            filterGroupsByRegionBalance(g.timeline),
          )
        : filteredGroupsIgnoringRegionBalance;
    }, [filterGroupsByRegionBalance, filteredGroupsIgnoringRegionBalance]);

    const { getTimelineOffsetTop, scrollToTimeline } = useTimelineHandlers(
      timelineRef,
      filteredGroups,
      timelineScroller,
    );

    const onToggleShipment = useCallback(
      (sId: number | ClientId, isCollapsed: boolean) => {
        const s = new Set(expandedShipmentIds);
        if (!isCollapsed) {
          s.delete(sId);
        } else {
          s.add(sId);
        }
        setExpandedShipmentIds(s);
      },
      [expandedShipmentIds],
    );

    const currencyRates = useCurrencyRates();
    const defaultCurrency = useDefaultCurrency();
    const { bunkerPriceByGradeName } = useBunkerPrices();

    const { getIncomeForLoadedAmount } = useMemo(() => {
      if (!pnlData?.incomingVoyageChartersById) {
        return {};
      }

      const incomingFreightByOrderId = getFreightsByOrderId(
        pnlData.incomingVoyageChartersById,
        orderById,
      );

      const getIncomeForLoadedAmount = incomeForLoadedAmountGetter(
        orderById,
        incomingFreightByOrderId,
        pnlData.incomingVoyageChartersById,
        currencyRates,
        defaultCurrency,
        bunkerPriceByGradeName,
        pnlData.worldscaleFreightRates,
      );

      return { getIncomeForLoadedAmount };
    }, [
      bunkerPriceByGradeName,
      currencyRates,
      defaultCurrency,
      orderById,
      pnlData?.incomingVoyageChartersById,
      pnlData?.worldscaleFreightRates,
    ]);

    const { locations, distances, portCalls } = scheduleContext;

    const assignedItems: ScenarioTimelineItem[] = useMemo(
      () =>
        buildAssignedItems({
          onToggleShipment,
          expandedShipmentIds,
          onSwapScheduleEntry,
          onSwapPortCall,
          sharedOrderIds,
          timelines,
          groups,
          timelineDiff,
          organizations,
          isCompressedView,
          getIncomeForLoadedAmount,
          distances,
          pnlData,
          charteringOpenPositions,
          vesselReservations,
          euEtsCostEnabled,
          orderById,
          fuelEuCostEnabled,
          pnlByShipmentId: currentScenarioPnl?.byShipmentId,
        }),
      [
        onToggleShipment,
        expandedShipmentIds,
        onSwapScheduleEntry,
        onSwapPortCall,
        sharedOrderIds,
        timelines,
        groups,
        timelineDiff,
        organizations,
        isCompressedView,
        getIncomeForLoadedAmount,
        distances,
        pnlData,
        charteringOpenPositions,
        vesselReservations,
        euEtsCostEnabled,
        orderById,
        fuelEuCostEnabled,
        currentScenarioPnl?.byShipmentId,
      ],
    );

    const contextItem: ScenarioTimelineItem | null = useMemo(
      () =>
        contextItemId
          ? (assignedItems.find((i) => i.id === contextItemId) ?? null)
          : null,
      [assignedItems, contextItemId],
    );

    const planningMode = usePlanningMode();

    const [expandedSuggestions, setExpandedSuggestions] = useState<Set<string>>(
      new Set(),
    );
    useEffect(() => {
      setExpandedSuggestions(new Set());
    }, [suggestions]);

    const [showNSuggestions, setShowNSuggestions] = useState(10);

    const suggestionItems = useSuggestionItems({
      suggestions,
      groups,
      showNSuggestions,
      expandedSuggestions,
      onSuggestionSelect,
      onDismissSuggestion,
      dismissedSuggestionIds,
      scheduleContext,
      planningMode,
      setExpandedSuggestions,
    });

    useEffect(() => {
      if (!suggestions) {
        setShowNSuggestions(10);
      }
    }, [suggestions]);

    const onShowMoreSuggestions = useCallback(() => {
      setShowNSuggestions((n) => n + 5);
    }, [setShowNSuggestions]);

    const completedItems = useCompletedItems({
      completedData,
      isCompressedView,
      locations,
    });

    const items = useMemo(() => {
      return [...assignedItems, ...suggestionItems];
    }, [assignedItems, suggestionItems]);

    const filteredItems = useFilteredItems({
      items,
      filteredGroups,
      spotAssignments,
      completedItems,
    });

    const itemById = useMemo(() => keyBy(items, (i) => i.id), [items]);
    const completedItemById = useMemo(
      () => keyBy(completedItems, (i) => i.id),
      [completedItems],
    );

    const handleItemContextMenu = useEvent(
      (itemId: ScenarioTimelineItem['id'], event: React.MouseEvent) => {
        const item = itemById[itemId] ?? completedItemById[itemId];
        if (item?.isShadow) return;

        if (!item) {
          throw new Error(`Timeline item not found by id`);
        }
        setContextItemId(itemId);
        switch (item.type) {
          case ItemType.EVENT:
            return onSingleEventContextMenu(event);
          case ItemType.OFFHIRE:
            return onOffHireContextMenu(event);
          case ItemType.OPEN:
            return onOpenPositionContextMenu(event);
          case ItemType.RESERVATION:
            return onVesselReservationContextMenu(event);
          case ItemType.SCHEDULED_ORDER:
            return onItemContextMenu(event);
          case ItemType.SPOT:
            return onItemContextMenu(event);
          case ItemType.COA:
            return onItemContextMenu(event);
          case ItemType.SUGGESTION:
          case ItemType.COMPLETED:
            return; // No context menu
          default:
            throw new Error(`Wrong item type for context menu handler`);
        }
      },
    );

    const handleViewItemDetails = useEvent(
      (itemId: ScenarioTimelineItem['id']) => {
        let item = filteredItems.find((x) => x.id === itemId);
        if (item?.isShadow) {
          const { originalItemId } = item;
          item = items.find((x) => x.id === originalItemId);
        }

        if (item && 'orders' in item) {
          onViewAssignment({
            timelineId:
              'groupObj' in item
                ? item.groupObj.timeline.id
                : item.group.toString(),
            orderIds: uniq(
              item.orders.map((o) => o.id).concat(Array.from(selectedOrderIds)),
            ),
          });
        } else if (item?.type === ItemType.OFFHIRE) {
          onViewOffHire(item.timeData.event.entry);
        } else if (item?.type === ItemType.OPEN) {
          const itemBeforeLast = items
            .filter((i) => i.group === item?.group)
            .sort((a, b) => a.end_time - b.end_time)
            .at(-2);
          onViewOpenPosition(item.openPosition, itemBeforeLast?.end_time);
        } else if (item?.type === ItemType.RESERVATION) {
          const itemBeforeLast = items
            .filter((i) => i.group === item?.group)
            .sort((a, b) => a.end_time - b.end_time)
            .at(-2);

          onViewVesselReservation(
            item.vesselReservation,
            itemBeforeLast?.end_time,
          );
        } else if (item?.type === ItemType.EVENT) {
          onViewSingleEvent(item.timeData.event, item.groupObj.timeline);
        }
      },
    );

    const [lastItemInTimeLineEnd, setLastItemInTimeLineEnd] = useState<
      number | undefined
    >(undefined);
    const [lastPort, setLastPort] = useState<number | undefined>(undefined);
    const [targetVesselName, setTargetVesselName] = useState('');

    const handleCanvasContextMenu = (
      groupId: number | string,
      time: number,
      event: React.MouseEvent,
    ) => {
      const targetTimeline = groups.find((g) => g.id === groupId);

      const lastItemInTimeLine = items
        .filter((i) => i.group === groupId)
        .sort((a, b) => a.end_time - b.end_time)
        .at(-1);
      if (targetTimeline?.timeline.type === 'tc') {
        setCurrentVesselId(targetTimeline.timeline.vesselId);
        setTargetVesselName(targetTimeline.timeline.vessel?.name ?? '');
        setLastItemInTimeLineEnd(lastItemInTimeLine?.end_time);
        const lastVoyageGroup =
          targetTimeline.timeline.voyageGroups.slice(-1)[0];
        const lastPortCall = lastVoyageGroup?.portCalls.slice(-1)[0];
        const locationId = lastPortCall?.locationId;
        setLastPort(locationId);
        setContextTime(time);
        onCanvasContextMenu(event);
      }
    };

    const { openModal } = useModal();

    const createOffHirePeriod = () => {
      if (currentVesselId) {
        openModal({
          type: 'EDIT_OFFHIRE',
          props: {
            vesselId: currentVesselId,
            initialStartTime: contextTime ?? undefined,
            onSave: onSaveOffHire,
          },
        });
      }
    };

    const createOpenPosition = () => {
      if (currentVesselId) {
        openModal({
          type: 'EDIT_OPEN_POSITION',
          props: {
            initialStartTime: contextTime ?? undefined,
            vesselId: currentVesselId,
            lastItemEnd: lastItemInTimeLineEnd,
            lastPortId: lastPort,
          },
        });
      }
    };

    const createVesselReserve = () => {
      if (currentVesselId) {
        openModal({
          type: 'VESSEL_RESERVATION',
          props: {
            vesselId: currentVesselId,
            vesselName: targetVesselName,
            lastItemEnd: lastItemInTimeLineEnd,
          },
        });
      }
    };

    const createSingleEvent = () => {
      if (currentVesselId) {
        const targetGroup = groups.find(
          (g) => g.timeline.vesselId === currentVesselId,
        );
        openModal({
          type: 'EDIT_SINGLE_EVENT',
          props: {
            vesselId: currentVesselId,
            initialStartTime: contextTime ?? undefined,
            handleCreateEvent: onCreateSingleEvent,
            timeline: targetGroup?.timeline,
          },
        });
      }
    };
    const forceSelectionChangeItems = useMemo(() => {
      const selectedOrderOrEventIds = assignedItems
        .filter(
          (item) =>
            ('orders' in item &&
              item.orders.some((o) => selectedOrderIds.has(o.id))) ||
            selectedEventIds.has(item.id),
        )
        .map((item) => item.id);

      return [...selectedOrderOrEventIds, ...selectedSuggestionIds];
    }, [
      assignedItems,
      selectedEventIds,
      selectedOrderIds,
      selectedSuggestionIds,
    ]);

    const [selectedItems, setSelectedItems] = useOverridableState<
      (number | string)[]
    >([], useDeepMemo(forceSelectionChangeItems));

    const selectedItemsOrders = useMemo(
      () =>
        items.flatMap((item) =>
          'orders' in item && selectedItems.includes(item.id)
            ? item.orders
            : [],
        ),
      [selectedItems, items],
    );

    const handleOptimizeVoyageSpeed = useEvent(() => {
      if (!onOptimizeVoyageSpeed) {
        return;
      }
      const contextItem = items.find((item) => item.id === contextItemId);
      if (!(contextItem && 'groupObj' in contextItem)) {
        return;
      }
      const timelineId = contextItem.groupObj.timeline.id;

      const allSelectedItemIds = new Set(
        compact([...selectedItems, contextItemId]),
      );

      // Ensure that all items to optimize are on the same timeline (user may have clicked items on several)
      const itemsToOptimize = items
        .filter((item) => allSelectedItemIds.has(item.id))
        .filter(
          (item) =>
            'groupObj' in item && item.groupObj.timeline.id === timelineId,
        );
      const eventsToOptimize = itemsToOptimize.flatMap((item) =>
        'events' in item ? item.events : [],
      );

      const portCallsToOptimize = new Set(
        compact(eventsToOptimize.map((td) => td.event.portCallId)),
      );

      onOptimizeVoyageSpeed({
        timelineId,
        portCallsToOptimize,
      });
    });

    const selectedOrders = useMemo(
      () => orders.filter((x) => selectedOrderIds.has(x.id)),
      [orders, selectedOrderIds],
    );

    const isOrderItemId = useCallback(
      (itemId: number | string): boolean => {
        const item = itemById[itemId];
        assertDefined(item, 'could not find item by id');

        return 'order' in item || 'orders' in item;
      },
      [itemById],
    );

    const isSingleEventItemId = useCallback(
      (itemId: number | string): boolean => {
        const item = itemById[itemId];
        assertDefined(item, 'could not find item by id');

        return item.type === ItemType.EVENT;
      },
      [itemById],
    );

    const isSuggestionItemId = useCallback(
      (itemId: number | string) => {
        const item = itemById[itemId];
        assertDefined(item, 'could not find item by id');

        return item.type === ItemType.SUGGESTION;
      },
      [itemById],
    );

    /** Gets all timeline items with orders on the same incoming voyage charter as the given orders */
    const getAllIncomingVoyageCharterItems = useCallback(
      (
        orderIds: number[],
      ): { itemIds: Array<string | number>; orderIds: Array<number> } => {
        const set = new Set(orderIds);
        const selectedOrders = orderIds.map((id) => orderById.get(id));
        const voyageCharters = new Set<number>();
        for (const order of selectedOrders) {
          const vcId = order?.incomingVoyageCharterRelation?.voyageCharterId;
          if (vcId) {
            voyageCharters.add(vcId);
          }
        }
        for (const order of orderById.values()) {
          const vcId = order?.incomingVoyageCharterRelation?.voyageCharterId;
          if (vcId && voyageCharters.has(vcId)) {
            set.add(order.id);
          }
        }

        const itemIds = items
          .filter((item) => {
            if ('orders' in item) {
              return item.orders.some((o) => set.has(o.id));
            }

            return false;
          })
          .map((item) => item.id);

        return {
          itemIds,
          orderIds: Array.from(set),
        };
      },
      [orderById, items],
    );

    const setSelectedItemsAndNotify = useCallback(
      (items: (number | string)[], lastClickedItemId: number | string) => {
        const lastClickedItem = itemById[lastClickedItemId];
        const groupObj =
          'groupObj' in lastClickedItem && lastClickedItem.groupObj;
        const originalOffsetY = groupObj
          ? getTimelineOffsetTop(groupObj.timeline)
          : null;

        const tds = assignedItems.filter((td) => items.includes(td.id));

        const selectedOrderIds = tds
          .flatMap((td) => {
            if ('orders' in td) {
              return td.orders;
            } else {
              return [];
            }
          })
          .map((o) => o.id);

        const singleEventIds = tds
          .filter((td) => td.type === ItemType.EVENT)
          .map((i) => i.id);

        const suggestionItems = items.filter(isSuggestionItemId);

        const { itemIds, orderIds } =
          getAllIncomingVoyageCharterItems(selectedOrderIds);

        onOrderSelectionChange([...orderIds]);
        onEventSelectionChange([...singleEventIds]);
        onSuggestionSelectionChange(suggestionItems);
        setSelectedItems([...itemIds, ...singleEventIds, ...suggestionItems]);

        if (originalOffsetY !== null && groupObj) {
          scrollToTimeline(groupObj.timeline, originalOffsetY);
        }
      },
      [
        assignedItems,
        getAllIncomingVoyageCharterItems,
        getTimelineOffsetTop,
        isSuggestionItemId,
        itemById,
        onEventSelectionChange,
        onOrderSelectionChange,
        onSuggestionSelectionChange,
        scrollToTimeline,
        setSelectedItems,
      ],
    );

    const handleResetItemToMaster = (
      itemId: ScenarioTimelineItem['id'],
      itemType: 'order' | 'shipment',
    ) => {
      const item = items.find((item) => item.id === itemId);
      assertDefined(item, 'item');

      if ('orders' in item && item.type !== ItemType.COMPLETED) {
        if (itemType === 'shipment') {
          onResetAssignmentsToMaster({
            shipmentIds: compact(
              uniq(item.orders.map((order) => order.shipmentId)),
            ),
          });
        } else {
          onResetAssignmentsToMaster({
            orderIds: item.orders.map((order) => order.id),
          });
        }
      } else {
        throw new Error(
          `Resetting items of type ${item.type} (id ${item.id}) not supported`,
        );
      }
    };

    const handleDeleteItem = (itemId: ScenarioTimelineItem['id']) => {
      const item = items.find((item) => item.id === itemId);
      assertDefined(item, 'item');

      if ('orders' in item && item.type !== ItemType.COMPLETED) {
        onDeleteAssignment(
          item.orders.map((order) => order.id),
          false,
          item.events
            .filter(isSingleEventTimeData)
            .map((e) => e.event.entry.clientId),
        );
      } else if (item.type === ItemType.RESERVATION) {
        void handleDeleteVesselReservation(item.vesselReservation);
      } else if (item.type === ItemType.OFFHIRE) {
        onDeleteOffHire(item.timeData.event.entry);
      } else if (item.type === ItemType.OPEN) {
        void handleDeleteOpenPosition(item.openPosition);
      } else if (item.type === ItemType.EVENT) {
        onDeleteSingleEvent(item.timeData.event);
      } else {
        throw new Error(
          `Deleting items of type ${item.type} (id ${item.id}) not supported`,
        );
      }
    };

    const handleCanvasClick = useCallback(() => {
      onOrderSelectionChange([]);
      onEventSelectionChange([]);
      onSuggestionSelectionChange([]);
      setSelectedItems([]);
    }, [
      onEventSelectionChange,
      onOrderSelectionChange,
      setSelectedItems,
      onSuggestionSelectionChange,
    ]);

    const handleItemSelect = useCallback(
      (itemId: number | string, e: React.MouseEvent) => {
        if (
          e.buttons === 0 ||
          e.button === 2 ||
          itemId.toString().includes('inactive') ||
          itemById[itemId]?.isShadow
        ) {
          return;
        }
        if (
          isOrderItemId(itemId) ||
          isSingleEventItemId(itemId) ||
          isSuggestionItemId(itemId)
        ) {
          let newItems;

          if (!e.shiftKey) {
            newItems = [itemId];
          } else {
            const newSelectionIsSuggestion = isSuggestionItemId(itemId);
            const attemptingToMixTypes = newSelectionIsSuggestion
              ? selectedItems.some((id) => !isSuggestionItemId(id))
              : selectedItems.some((id) => isSuggestionItemId(id));

            newItems = attemptingToMixTypes
              ? [itemId]
              : [...selectedItems, itemId];
          }

          setSelectedItemsAndNotify(newItems, itemId);
        }
      },
      [
        isOrderItemId,
        isSingleEventItemId,
        isSuggestionItemId,
        itemById,
        selectedItems,
        setSelectedItemsAndNotify,
      ],
    );

    const handleItemClick = useCallback(
      (itemId: number | string, e: React.MouseEvent) => {
        if (
          e.button === 2 ||
          itemId.toString().includes('inactive') ||
          itemById[itemId]?.isShadow
        ) {
          return;
        }
        if (
          isOrderItemId(itemId) ||
          isSingleEventItemId(itemId) ||
          isSuggestionItemId(itemId)
        ) {
          let newItems;

          if (!e.shiftKey) {
            newItems =
              selectedItems.length === 1 && selectedItems[0] === itemId
                ? []
                : [itemId];
          } else {
            const isSuggestion = isSuggestionItemId(itemId);
            const attemptingToMixTypes = isSuggestion
              ? selectedItems.some((id) => !isSuggestionItemId(id))
              : selectedItems.some((id) => isSuggestionItemId(id));

            newItems = attemptingToMixTypes
              ? [itemId]
              : selectedItems.filter((id) => id !== itemId);
          }

          setSelectedItemsAndNotify(newItems, itemId);
        }
      },
      [
        isOrderItemId,
        isSingleEventItemId,
        isSuggestionItemId,
        itemById,
        selectedItems,
        setSelectedItemsAndNotify,
      ],
    );

    const handleSuggestVoyageClick = useCallback(() => {
      if (currentVesselId === null) {
        return;
      }

      onSuggestVoyage(currentVesselId);
    }, [currentVesselId, onSuggestVoyage]);

    useImperativeHandle(ref, () => ({
      scrollToToday: () => scrollToToday(timelineRef),
      getTimelineOffsetTop,
      collapseExpandedVoyages,
    }));

    /** Note that itemRenderer changing does not currenly trigger re-rendering of the timeline components.
     * That behavior can be changed by updating the shouldComponentUpdate functions in the timeline library,
     * but this can cause performance issues and shouldn't be done without careful performance testing.
     * TODO: check if any of the item renderer parameters require re-rendering of the timeline components immediately, there may be some dormant related bugs there */
    const itemRenderer = useMemo(
      () =>
        makeTimelineItemRenderer({
          getTimelineOffsetTop,
          locations,
          portCallsById: portCalls,
          onAddOrderToAssignment,
          onChangeOrdersVoyage,
          scrollToTimeline,
          showNSuggestions,
          onShowMoreSuggestions,
          observer,
          isSuggestAvailable,
          onSuggestVoyage,
          onViewAssignment,
          showInquiries: planningMode === 'voyage_charter',
          shipmentIntegrationStatus,
          isReadOnlyScenario,
          isMobileView,
          suggestions,
        }),
      [
        getTimelineOffsetTop,
        locations,
        portCalls,
        onAddOrderToAssignment,
        onChangeOrdersVoyage,
        scrollToTimeline,
        showNSuggestions,
        onShowMoreSuggestions,
        observer,
        isSuggestAvailable,
        onSuggestVoyage,
        onViewAssignment,
        planningMode,
        shipmentIntegrationStatus,
        isReadOnlyScenario,
        isMobileView,
        suggestions,
      ],
    );

    const focusToOrders = useCallback(
      (orderIds: number[]) => {
        if (!timelineRef.current) return;
        scrollToOrders(orderIds, timelineRef.current, timelineScroller);
      },
      [timelineScroller],
    );

    const canResetContextOrder = !!(
      contextItem &&
      'combinedDiff' in contextItem &&
      contextItem.combinedDiff?.hasChange
    );

    const [selectedRegion, setSelectedRegion] = useState<string | null>(null);

    const { data: regionsQueryData } = useAllRegionsQuery();

    const vesselsForRegions = useMemo(
      () =>
        new Map(
          filteredMap(filteredGroupsIgnoringRegionBalance, (g) =>
            g.timeline.vessel && !g.timeline.vessel.externalFleet
              ? [g.timeline.vessel.id, g.timeline.vessel]
              : NONE,
          ),
        ),
      [filteredGroupsIgnoringRegionBalance],
    );

    const regionOpenData = useMemo(
      () =>
        calculateRegionOpenData({
          regions: compact(regionsQueryData?.allRegions?.nodes),
          orders,
          items,
          vessels: vesselsForRegions,
          timelines,
          locations,
          regionsByLocation,
        }),
      [
        items,
        locations,
        orders,
        regionsByLocation,
        regionsQueryData?.allRegions?.nodes,
        timelines,
        vesselsForRegions,
      ],
    );

    const headerData: RegionHeaderData = useMemo(
      () => ({
        regionPolygons,
        locations,
        regionBalanceFilter,
        setRegionBalanceFilter,
        showRegions,
        setShowRegions,
        selectedRegion,
        timelines,
        vesselReservations,
        regionOpenData,
      }),
      [
        regionPolygons,
        locations,
        regionBalanceFilter,
        setRegionBalanceFilter,
        showRegions,
        selectedRegion,
        timelines,
        vesselReservations,
        regionOpenData,
      ],
    );

    useEffect(() => {
      if (showRegions && timelineRef.current && timelineScroller) {
        const { offsetTop } = getSumOffset(timelineScroller);
        const { groupTops } = timelineRef.current.state;
        const lastGroupTop = groupTops.pop();
        timelineScroller.scrollTop = (lastGroupTop ?? 0) + offsetTop;
      }
    }, [showRegions, timelineRef, timelineScroller]);

    const [regionToolInterval] = useRegionToolIntervalState();

    const dateHeaderHeight = 56;
    const regionToggleHeight = 20;
    const availableRegionsCount = uniq([
      ...regionOpenData.regionPairsWithOpenData.keys(),
      ...regionOpenData.topLevelRegionsWithOpenData.keys(),
    ]).length;
    const availableRegionPairsCount =
      selectedRegion !== null
        ? regionOpenData.regionPairsWithOpenData.get(selectedRegion).size
        : 0;
    const regionsHeight =
      31 *
      (showRegions ? availableRegionsCount + availableRegionPairsCount : 0);
    const headerHeight =
      dateHeaderHeight +
      (availableRegionsCount > 0 ? regionsHeight + regionToggleHeight : 0);

    const dropSpec = {
      accept: ['orders', 'shipments', 'single_event'],
      onDragOver: (e) => {
        if (e.ctrlKey) {
          setDropEffect('link', 'Place directly at mouse position');
        } else if (shouldWaitForLaycan(e.shiftKey, isWaitLaycanDefault)) {
          setDropEffect('move', 'Place at start of laycan');
        } else {
          setDropEffect('copy', 'Place at next possible time');
        }
      },
      onDragLeave: () => {
        setDropEffect('none', '');
      },
      onDrop: (e) => {
        const item = getDropData(e, dragDropDataSchema.parse);
        if (!item) {
          return;
        }

        const timeline = timelineRef.current;
        const pageX = e.clientX;
        const pageY = e.clientY;
        const initialSidebarWidth = timeline?.props.sidebarWidth;

        if (!timeline || !pageX || !pageY || !initialSidebarWidth) return;

        const { canvasTimeStart, groupTops, width, sidebarWidth, timeRatio } =
          timeline.state;

        const { offsetLeft, offsetTop } = getSumOffset(timelineScroller);
        const { scrollLeft, scrollTop } = getSumScroll(timelineScroller);

        const x =
          pageX -
          offsetLeft +
          scrollLeft -
          (sidebarWidth || initialSidebarWidth);
        const y = pageY - offsetTop + scrollTop - headerHeight;

        const start = x * timeRatio + canvasTimeStart;
        const droppedStartDate = +snapTimeTo1HGrid(start);

        let groupKey = '';
        for (const key of Object.keys(groupTops)) {
          const groupTop = groupTops[Number(key)];
          if (y > groupTop) {
            groupKey = key;
          } else {
            break;
          }
        }

        if (
          groupKey === '' ||
          pageX < offsetLeft ||
          pageX > offsetLeft + width
        ) {
          return;
        }

        const isCtrlPressed = e.ctrlKey || e.metaKey;
        const waitForLaycan = shouldWaitForLaycan(
          e.shiftKey,
          isWaitLaycanDefault,
        );

        setTimeout(() => {
          const group = filteredGroups[Number(groupKey)];
          const originalOffset = getTimelineOffsetTop(group.timeline);

          if (item.type === 'orders') {
            // Orders have been dragged from order table onto a timeline
            const orderIds = getDroppedOrderIds(item);
            const { orderIds: allOrderIds } =
              getAllIncomingVoyageCharterItems(orderIds);

            const assignedIds = new Set<number>();
            for (const item of assignedItems) {
              if ('orders' in item) {
                for (const order of item.orders) {
                  assignedIds.add(order.id);
                }
              }
            }
            const existingOrderIds = allOrderIds.filter((id) =>
              assignedIds.has(id),
            );

            if (
              existingOrderIds.length > 0 &&
              allOrderIds.length !== existingOrderIds.length
            ) {
              const all = allOrderIds.join(', ');
              const existing = existingOrderIds.join(', ');
              const msg = `Trying to create new assignments for orders #${all} but ${existing} are already assigned, either add the new orders to that voyage, or un-assign the old ones first`;
              toast(msg, {
                type: toast.TYPE.ERROR,
                isLoading: false,
              });
              return;
            }

            // Pre-sorting dropped orders by layday creates a meaningful initial port rotation
            const orders = sortBy(
              allOrderIds.map((id) =>
                assertedDefined(orderById.get(id), `dropped order ${id}`),
              ),
              (o) => dateStringToNumOpt(o.layday) ?? Infinity,
            );

            const isCoa = group.timeline.type === 'coa';
            const coa = isCoa ? group.timeline.coa : null;
            if (coa) {
              const vesselCoaAssortmentIds = new Set<number>();
              const vesselCoaAssortmentGroupIds = new Set<number>();
              let hasCatchAll = false;

              for (const assortment of coa.coaAssortments.nodes) {
                const { assortmentId, assortmentGroupId } = assortment || {};
                const catchAll =
                  assortmentId === null && assortmentGroupId === null;
                if (catchAll) {
                  hasCatchAll = true;
                  continue;
                }
                if (assortmentId) vesselCoaAssortmentIds.add(assortmentId);
                if (assortmentGroupId)
                  vesselCoaAssortmentGroupIds.add(assortmentGroupId);
              }

              const nonContractedAssortments = hasCatchAll
                ? []
                : orders.filter((order) => {
                    const assortmentId = order.assortmentId;
                    const assortmentGroupId =
                      order.assortmentByAssortmentId?.assortmentGroup;
                    return (
                      assortmentId &&
                      !vesselCoaAssortmentIds.has(assortmentId) &&
                      assortmentGroupId &&
                      !vesselCoaAssortmentGroupIds.has(assortmentGroupId)
                    );
                  });

              if (nonContractedAssortments.length) {
                const assortmentNames = nonContractedAssortments.map(
                  (order) => order.assortmentByAssortmentId?.name ?? '',
                );
                toast(
                  `[${assortmentNames.join(', ')}] ${
                    assortmentNames.length > 1 ? `aren't` : `isn't`
                  } listed in the COA contract`,
                  {
                    type: toast.TYPE.ERROR,
                    isLoading: false,
                  },
                );

                return;
              }
            }

            const earliestLayday = orders
              .map((o) => dateStringToNumOpt(o.layday))
              .reduce((a, b) => (a && b ? Math.min(a, b) : (b ?? a)), null);

            const startIsOverride = isCtrlPressed;

            const startDate = startIsOverride
              ? droppedStartDate
              : waitForLaycan && earliestLayday
                ? Math.max(droppedStartDate, earliestLayday)
                : droppedStartDate;

            const timeline = group.timeline;

            if (timeline.type === 'tc') {
              onCreateAssignment(
                orders.map((order, i) => ({
                  orderId: order.id,
                  startDate: new Date(startDate + i),
                  vesselId: timeline.vesselId,
                })),
                {
                  shipWithOrderId: null,
                  voyagePlacement: startIsOverride
                    ? 'exact'
                    : waitForLaycan
                      ? 'laycan'
                      : 'asap',
                },
              );
            } else if (timeline.type === 'spot') {
              onCreateSpotAssignment(
                orders.map((order, i) => ({
                  orderId: order.id,
                  startDate: new Date(startDate + i),
                  ownerOrganizationId: timeline.ownerOrganizationId,
                  vesselId: timeline.vesselId,
                })),
                { shipWithOrderId: null, startIsOverride: true },
              );
            } else if (timeline.type === 'coa') {
              onCreateCoaAssignment(
                orders.map((order, i) => ({
                  orderId: order.id,
                  startDate: new Date(startDate + i),
                  coaId: timeline.coaId,
                  vesselId: timeline.vesselId,
                })),
                { shipWithOrderId: null, startIsOverride: true },
              );
            }
          } else if (item.type === 'shipments') {
            // A voyage has been dragged from timeline to another or along one
            const targetGroup = filteredGroups[Number(groupKey)];
            const startIsOverride =
              isCtrlPressed ||
              (targetGroup.timeline.type !== 'tc' && !waitForLaycan);
            changeOrdersVoyage(
              new Set(item.orderIds),
              targetGroup.timeline.id,
              {
                startDate: new Date(droppedStartDate),
                shipWithOrderId: null,
                voyagePlacement: startIsOverride
                  ? 'exact'
                  : waitForLaycan
                    ? 'laycan'
                    : 'asap',
                isWholeShipment: item.isWholeShipment,
                singleEventIds: item.singleEventIds,
              },
            );
          } else if (item.type === 'single_event') {
            // A voyage has been dragged from timeline to another or along one
            const targetTimelineId =
              filteredGroups[Number(groupKey)].timeline.id;
            onMoveSingleEvent(targetTimelineId, [item.singleEventId], {
              startDate: new Date(droppedStartDate),
            });
          }
          scrollToTimeline(group.timeline, originalOffset);
        });
      },
    } as UseDropProps;

    const { props: dropProps } = useDrop(dropSpec);

    const clientId =
      contextItem && 'events' in contextItem
        ? contextItem.orders?.at(0)?.shipmentId
        : null;

    const openSuggestionEstimator = useOpenSuggestionEstimator();

    const onOpenEstimator = () => {
      openSuggestionEstimator({
        selectedSuggestionIds,
        items,
        onSuggestionAccept: onSuggestionSelect,
      });
    };

    const contextMenuStore = useContextStore<VoyageMenuContextValue>({
      setContextItemId,
      getItemMenuButtonProps,
    });

    return (
      <View flex="1" height="inherit">
        {suggestions && (
          <SuggestionControls
            clearSuggestions={onClearSuggestions}
            showSuggestionReport={onOpenVoyageSuggestionReport}
            onOpenEstimator={onOpenEstimator}
            hasSelectedSuggestions={selectedSuggestionIds.size > 0}
          />
        )}
        <TimelineStyle />
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: '100%',
          }}
          {...dropProps}
        >
          {!filteredGroups.length && <NotFoundTimelineItems />}

          <VoyageMenuContext.Provider value={contextMenuStore}>
            <TimelineView<ScenarioTimelineItem, ScenarioTimelineGroup>
              timelineRef={timelineRef}
              scrollRef={timelineScrollRef}
              itemTouchSendsClick={false}
              groups={filteredGroups}
              items={filteredItems}
              selected={selectedItems}
              groupRenderer={groupRenderer}
              itemRenderer={itemRenderer}
              onItemContextMenu={handleItemContextMenu}
              onCanvasClick={handleCanvasClick}
              onItemSelect={handleItemSelect}
              onItemClick={handleItemClick}
              onCanvasContextMenu={handleCanvasContextMenu}
              onItemDoubleClick={handleViewItemDetails}
              canMove={false} // We use our own dragging implementation
              headerHeight={headerHeight}
              sidebarWidth={isMobileView ? 100 : 200}
              lineHeight={
                isCompressedView ? 28 : isMobileView && suggestions ? 105 : 54
              }
              resizeDetector={TimelineResizeDetector}
              isReadOnlyScenario={isReadOnlyScenario}
            >
              <FocusedOrdersListener focusToOrders={focusToOrders} />
              {selectedOrders.length && (
                <Box
                  position="absolute"
                  bottom={
                    regions.length > 1
                      ? regionToggleHeight + (showRegions ? regionsHeight : 0)
                      : 0
                  } // This will move the loading/discharging indicator up/down in ruler if there are regions
                  opacity={0.6}
                  overflow="hidden"
                >
                  <CustomHeader
                    height={8}
                    headerData={{ items, selectedOrders }}
                    unit="hour"
                  >
                    {({
                      headerContext: { intervals },
                      getRootProps,
                      showPeriod,
                    }: CustomHeaderRendererProps<{
                      items: ScenarioTimelineItem[];
                      selectedOrders: ScenarioOrder[];
                    }>): JSX.Element | null => {
                      if (intervals.length < 1) {
                        // Due to an issue in timeline library intervals can sometimes be empty
                        // after page change in next
                        return null;
                      }

                      const loadingWindows = selectedOrders.flatMap(
                        ({ cancelling, layday }: ScenarioOrder) => {
                          if (!layday || !cancelling) return [];
                          return [
                            {
                              start: Date.parse(layday),
                              end: Date.parse(cancelling),
                            },
                          ];
                        },
                      );
                      const dischargeWindows = selectedOrders.flatMap(
                        ({ deliveredBefore, rta }: ScenarioOrder) => {
                          if (!rta || !deliveredBefore) return [];
                          return [
                            {
                              start: Date.parse(rta),
                              end: Date.parse(deliveredBefore),
                            },
                          ];
                        },
                      );

                      const pxPerMs = timeToCoordinateRatio(
                        intervals[0].startTime.valueOf(),
                        intervals[0].endTime.valueOf(),
                        intervals[0].labelWidth,
                      );

                      const left = intervals[0].startTime.valueOf();
                      const timestampToLeft = (ts: number): number => {
                        return (ts - left) * pxPerMs + (intervals[0].left ?? 0);
                      };
                      const durationToWidth = (duration: number): number => {
                        return duration * pxPerMs;
                      };

                      const renderLoadingDischargingWindow = (
                        ldWindow: (typeof loadingWindows)[number],
                        isLoading: boolean,
                        index: number,
                      ) => {
                        const left = timestampToLeft(ldWindow.start);
                        const width = durationToWidth(
                          ldWindow.end - ldWindow.start,
                        );

                        const bgColor = isLoading
                          ? indicatorTypeToColor.load
                          : !isLoading
                            ? indicatorTypeToColor.discharge
                            : 'transparent';
                        return (
                          <Box
                            left={left}
                            width={width}
                            position="absolute"
                            onClick={() => {
                              showPeriod(ldWindow.start, ldWindow.end);
                            }}
                            backgroundColor={bgColor}
                            key={`overlapped-${
                              isLoading ? 'load' : 'discharge'
                            }-${index}`}
                            zIndex={isLoading ? 1 : -1}
                          >
                            <Box className="sticky">&nbsp;</Box>
                          </Box>
                        );
                      };

                      return (
                        <Box {...getRootProps()}>
                          {loadingWindows.map((loadingWindow, i) =>
                            renderLoadingDischargingWindow(
                              loadingWindow,
                              true,
                              i,
                            ),
                          )}
                          {dischargeWindows.map((dischargeWindow, i) =>
                            renderLoadingDischargingWindow(
                              dischargeWindow,
                              false,
                              i,
                            ),
                          )}
                        </Box>
                      );
                    }}
                  </CustomHeader>
                </Box>
              )}
              {regions.length > 1 ? (
                <CustomHeader
                  headerData={headerData}
                  height={regionsHeight + regionToggleHeight}
                  unit={regionToolInterval === 'weeks' ? 'isoWeek' : 'month'}
                >
                  {showRegions ? RegionHeaderIntervals : RegionHeaderToggle}
                </CustomHeader>
              ) : null}
              {regions.length > 1 ? (
                <SidebarHeader>
                  {({ getRootProps }) => {
                    return (
                      <SidebarContainer {...getRootProps()}>
                        {showRegions ? (
                          <RegionsListHeaderSidebar
                            regions={regions}
                            regionOpenData={regionOpenData}
                            selectedRegion={selectedRegion}
                            setSelectedRegion={setSelectedRegion}
                          />
                        ) : null}
                        <RegionRow
                          style={{ height: 20, backgroundColor: '#607c94' }}
                        />
                      </SidebarContainer>
                    );
                  }}
                </SidebarHeader>
              ) : null}
            </TimelineView>
          </VoyageMenuContext.Provider>
        </div>
        <div ref={setItemMenuRef} style={itemMenuStyles}>
          {isItemMenuVisible && contextItem !== null && (
            <ItemContextMenu
              contextItemId={contextItemId}
              contextItem={contextItem}
              expandedShipmentIds={expandedShipmentIds}
              shouldShowSuggestMoreCargo={shouldShowSuggestMoreCargo}
              selectedItems={selectedItems}
              selectedItemsOrders={selectedItemsOrders}
              canResetContextOrder={canResetContextOrder}
              clientId={clientId}
              scenario={scenario}
              handleViewItemDetails={handleViewItemDetails}
              hideItemContextMenu={hideItemContextMenu}
              setExpandedShipmentIds={setExpandedShipmentIds}
              onRemoveOrderFromShipment={onRemoveOrderFromShipment}
              onSuggestVoyage={onSuggestVoyage}
              onPublishOrder={onPublishOrder}
              onMergeToShipment={onMergeToShipment}
              onRearrangeShipmentEvents={onRearrangeShipmentEvents}
              handleDeleteItem={handleDeleteItem}
              handleResetItemToMaster={handleResetItemToMaster}
              onSetVoyageAttribute={onSetVoyageAttribute}
              onOptimizeVoyageSpeed={handleOptimizeVoyageSpeed}
            />
          )}
        </div>

        <div ref={setOffHireMenuRef} style={offHireMenuStyles}>
          {isOffHireMenuVisible && (
            <Menu hasShadow>
              <MenuItem
                label="Edit off-hire period"
                onPress={() => {
                  if (contextItemId !== null) {
                    handleViewItemDetails(contextItemId);
                    hideOffHireMenu();
                  }
                }}
              />
              <MenuItem
                label="Remove off-hire period"
                onPress={() => {
                  if (contextItemId !== null) {
                    handleDeleteItem(contextItemId);
                    hideOffHireMenu();
                  }
                }}
              />
            </Menu>
          )}
        </div>

        <div ref={setVesselReservationRef} style={vesselReservationMenuStyles}>
          {isVesselReservationVisible && (
            <Menu hasShadow>
              <MenuItem
                label="Edit vessel reservation"
                onPress={() => {
                  if (contextItemId !== null) {
                    handleViewItemDetails(contextItemId);
                    hideVesselReservation();
                  }
                }}
              />
              <MenuItem
                label="Remove vessel reservation"
                onPress={() => {
                  if (contextItemId !== null) {
                    handleDeleteItem(contextItemId);
                    hideVesselReservation();
                  }
                }}
              />
            </Menu>
          )}
        </div>

        <div ref={setOpenPositionMenuRef} style={openPositionMenuStyles}>
          {isOpenPositionMenuVisible && (
            <Menu hasShadow>
              <MenuItem
                label="Edit open position"
                onPress={() => {
                  if (contextItemId !== null) {
                    handleViewItemDetails(contextItemId);
                    hideOpenPositionMenu();
                  }
                }}
              />
              <MenuItem
                label="Remove open position"
                onPress={() => {
                  if (contextItemId !== null) {
                    handleDeleteItem(contextItemId);
                    hideOpenPositionMenu();
                  }
                }}
              />
            </Menu>
          )}
        </div>

        <div ref={setSingleEventMenuRef} style={singleEventMenuStyles}>
          {isSingleEventMenuVisible && (
            <Menu hasShadow>
              <MenuItem
                label="Edit single event"
                onPress={() => {
                  if (contextItemId !== null) {
                    handleViewItemDetails(contextItemId);
                    hideSingleEventMenu();
                  }
                }}
              />
              <MenuItem
                label="Remove single event"
                onPress={() => {
                  if (contextItemId !== null) {
                    handleDeleteItem(contextItemId);
                    hideSingleEventMenu();
                  }
                }}
              />
            </Menu>
          )}
        </div>

        <div ref={setCanvasMenuRef} style={canvasMenuStyles}>
          {isCanvasMenuVisible && (
            <Menu hasShadow>
              <MenuItem
                label="Reserve vessel"
                onPress={() => {
                  createVesselReserve();
                  hideCanvasMenu();
                }}
              />
              <MenuItem
                label="Create open position"
                onPress={() => {
                  createOpenPosition();
                  hideCanvasMenu();
                }}
              />
              <MenuItem
                label="Create off-hire period"
                onPress={() => {
                  createOffHirePeriod();
                  hideCanvasMenu();
                }}
              />
              <MenuItem
                label="Create single event"
                onPress={() => {
                  createSingleEvent();
                  hideCanvasMenu();
                }}
              />
              {isSuggestAvailable && (
                <>
                  <MenuItem
                    label="Suggest voyage"
                    onPress={() => {
                      handleSuggestVoyageClick();
                      hideCanvasMenu();
                    }}
                  />
                </>
              )}
            </Menu>
          )}
        </div>

        <div ref={setVesselMenuRef} style={vesselMenuStyles}>
          {isVesselMenuVisible && (
            <Menu hasShadow>
              <MenuItem
                label={`Set ${
                  timelines.find(
                    (timeline) => timeline.vesselId === currentVesselId,
                  )?.vessel?.name
                } as inactive`}
                onPress={() => {
                  setShowVesselPrompt({
                    deactivateVessel: true,
                    modifyVesselToFleet: false,
                  });
                }}
              />
              <MenuItem
                label={
                  currentVesselInFleet?.isVesselInFleet
                    ? `
                  Remove ${currentVesselInFleet?.vesselName} from own fleet`
                    : `Add ${currentVesselInFleet?.vesselName} to own fleet`
                }
                onPress={() => {
                  setShowVesselPrompt({
                    deactivateVessel: false,
                    modifyVesselToFleet: true,
                  });
                }}
              />
            </Menu>
          )}
        </div>

        {showVesselPrompt.deactivateVessel && (
          <DeactivateVesselModal
            onClose={() =>
              setShowVesselPrompt({
                deactivateVessel: false,
                modifyVesselToFleet: false,
              })
            }
            vesselId={currentVesselId}
            vesselName={
              timelines.find(
                (timeline) => timeline.vesselId === currentVesselId,
              )?.vessel?.name
            }
          />
        )}

        {showVesselPrompt.modifyVesselToFleet && (
          <ModifyVesselToFleetModal
            onClose={() =>
              setShowVesselPrompt({
                deactivateVessel: false,
                modifyVesselToFleet: false,
              })
            }
            vesselId={currentVesselId}
            vesselName={
              timelines.find(
                (timeline) => timeline.vesselId === currentVesselId,
              )?.vessel?.name
            }
            type={currentVesselInFleet?.isVesselInFleet ? 'remove' : 'add'}
          />
        )}
      </View>
    );
  },
);

/**
 * Debug view for testing IMOS TC Contract import
 * TODO: Remove when actual frontend is implemented
 */
function ImosTcContractsDebug(props: {
  locationById: ScheduleDataContext['locations'];
  vessel: Vessel;
}) {
  const isImosTcContractsEnabled = hasFeature(
    'imos_graphql_tc_contract_import',
  );
  if (!isImosTcContractsEnabled) return null;

  const tcContracts = compact(props.vessel.tcContractsByVesselId.nodes);
  if (tcContracts.length < 1) return null;

  const DebugInfo = (
    <div>
      {tcContracts.map((tc) => {
        return (
          <Box key={tc.externalId} mb={2}>
            <strong>External id:</strong> {tc.externalId}
            <br />
            <strong>Delivery port:</strong>{' '}
            {props.locationById.get(tc.deliveryPortId)?.name}
            <br />
            <strong>Earliest delivery date:</strong>{' '}
            <TimeField value={tc.deliveryStartDate} />
            <br />
            <strong>Latest delivery date:</strong>{' '}
            <TimeField value={tc.deliveryEndDate} />
            <br />
            <strong>Redelivery port:</strong>{' '}
            {props.locationById.get(tc.redeliveryPortId)?.name}
            <br />
            <strong>Earliest redelivery date:</strong>{' '}
            <TimeField value={tc.redeliveryStartDate} />
            <br />
            <strong>Latest redelivery date:</strong>{' '}
            <TimeField value={tc.redeliveryEndDate} />
            <br />
          </Box>
        );
      })}
    </div>
  );

  return (
    <Tooltip content={DebugInfo}>
      <span>
        <Text fontSize={1} ml={1}>
          TC*
        </Text>
      </span>
    </Tooltip>
  );
}

ScenarioTimeline.displayName = 'ScenarioTimeline';
export default ScenarioTimeline;
